/**
 * The Body Map screen (the Analytics view): the whole body, every part lit by
 * how it did, and a sheet for one part with what was measured, a replay of the
 * player's own skeleton they can scrub and turn, their trend, one drill and
 * the part's badge.
 *
 * Spec: BODY-MAP.md section 3. The maths and every word live in
 * ../shared/bodyparts.js (pure, checked by scripts/bodyparts-test.mjs). This
 * file only draws: plain SVG for the figure and the trend chart, and a 2D
 * canvas with a small hand written 3D projection for the replay. No library.
 *
 * Rules that run through it (HANDOFF.md):
 * - A part is colored only from verdict readings (confidence 0.6 or more, and
 *   3 shots in a session). Anything less is grey and says why in words.
 * - A status is never color alone: each one also has a ring style and a glyph.
 * - Nothing runs while idle. The replay draws only while it is dragged, played
 *   or scrubbed, and nothing runs at all while the sheet is closed.
 * - Strict CSP: no inline handlers, no inline scripts, no eval. Every listener
 *   is added here with addEventListener.
 * - Numbers only. A replay is landmark numbers from the snapshot, never video.
 *
 * API
 *   mount(ctx)               optional, from app.js: { api }
 *   render(container, data)  draws the whole screen; safe to call again
 *   open(partId)             opens a part, returns close()
 *   show()                   for go('body'): GET /api/bodymap, then render into #v-body
 *   celebrate(list)          queue celebrations [{ part, from, to, xp }], [{ badge }] or [{ badges }]
 *   placement()              where the player said the phone is, for app.js onShot
 *   stats                    the last render, open and replay frame timings (ms)
 * Events, dispatched on the container and bubbling:
 *   bodymap:drill      { part, skill }   "Try this drill" was tapped
 *   bodymap:placement  { placement }     'rim' | 'side' | 'other' was chosen
 */

import {
  PARTS, PART_IDS, COPY, SAFETY, BODY, MOMENTS, BONES, HAND_BONES, KEEP_POINTS, MODE_PARTS, PART_BADGE,
  partById, bandsOf, measuredLine, sessionLine, headlineFor, tipFor, reasonLine, sureWord, statusOf,
  partSummary, unpackSnapshot, detectView, angle3, sidePoints, fillCopy as fill,
} from '../shared/bodyparts.js';

/* ======================================================================= *
 *  CONSTANTS                                                               *
 * ======================================================================= */
const PLACEMENT_KEY = 'hb.bodymap.placement';        // BODY-MAP.md 1.5, a per device habit
const BASELINE_KEY = 'hb.pose.baseline.v2';          // public/js/posemetrics.js BASELINE_KEY: calibrated when present
const SKY = '#2EA8FF';                               // ideal lines, BODY-MAP.md 3.3 (--cool at night)
const DEG = Math.PI / 180;
const CAM_D = 3.5;                                   // the replay camera, metres back (3.5)
const TILT_DRAG = 30;                                // a drag tilts at most this far (3.5)
const TILT_TOP = 85;
const DEPTH_NOTE_AT = 35;                            // "Depth is my best guess" past this turn (1.8)
const PLAY_MS = 2400;
const CELEBRATE_MS = 3400;
const LAYOUT = { tablet: 690, desk: 980 };           // container widths: phone below 690, desk from 980

/**
 * Hotspots on the right handed figure, viewBox 0 0 360 520 (BODY-MAP.md 3.3).
 * Body parts get a hit area of at least 26 units; the small ones live in the
 * hand and foot bubbles, which magnify them.
 */
const SPOT = {
  head: { kind: 'circle', x: 180, y: 56, r: 30 },
  shoulders: { kind: 'pill', x: 180, y: 118, w: 110, h: 30, hitH: 40 },
  elbow: { kind: 'circle', x: 118, y: 92, r: 17, hit: 26 },
  core: { kind: 'rect', x: 180, y: 182, w: 62, h: 58 },
  hips: { kind: 'pill', x: 180, y: 250, w: 100, h: 30, hitH: 40 },
  knees: { kind: 'pair', pts: [[158, 330], [202, 330]], r: 17, hit: 25 },
  fingers: { kind: 'circle', x: 50, y: 36, r: 22, bubble: true },
  wrist: { kind: 'circle', x: 74, y: 82, r: 21, bubble: true },
  ankles: { kind: 'circle', x: 284, y: 422, r: 20, bubble: true },
  heels: { kind: 'circle', x: 270, y: 470, r: 20, bubble: true },
  toes: { kind: 'circle', x: 328, y: 470, r: 20, bubble: true },
};
const BUBBLE = { hand: { x: 58, y: 58, r: 52 }, foot: { x: 300, y: 452, r: 56 } };

/**
 * The figure's own order, top to bottom: it is the tab order on the figure, and
 * later spots sit on top, so the elbow wins a tap where it meets the shoulders
 * and the bubble wins where it meets the elbow.
 */
const FIG_ORDER = ['head', 'shoulders', 'elbow', 'wrist', 'fingers', 'core', 'hips', 'knees', 'ankles', 'heels', 'toes'];

/** The moment each part's replay opens on: where that part does its job. */
const KEY_MOMENT = { fingers: 'follow', wrist: 'set', elbow: 'set', shoulders: 'set', head: 'release', core: 'release', hips: 'land', knees: 'load', ankles: 'takeoff', heels: 'takeoff', toes: 'land' };

/* ======================================================================= *
 *  STATE. The open part and the viewer angles; the rest is the last render. *
 * ======================================================================= */
let ctx = null;
let root = null;                 // the container render() drew into
let D = null;                    // the data render() was given, normalised
let M = null;                    // the view model for the current mode and range
// picked: the player chose ui.part; until then the desk detail follows "Next up"
const ui = { mode: 'shoot', range: 'last', part: null, picked: false, sheet: false, shot: 'last', opener: null, layout: 'phone' };
const cam = { yaw: 0, tilt: 0 };
let replay = null;
let replayToken = 0;             // a replay still waiting for its frame is dropped when this moves on
/** Stop the replay, and any replay still waiting to be built. */
function dropReplay() { replayToken++; if (replay) { replay.destroy(); replay = null; } }
let ro = null;
const played = new Set();        // celebration keys already shown by this page
const queue = [];
let celebrating = false;
export const stats = { renderMs: null, openMs: null, drawMs: null, drawMax: 0, draws: 0 };

/* ======================================================================= *
 *  SMALL HELPERS                                                           *
 * ======================================================================= */
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const fin = Number.isFinite;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const wrap180 = (a) => ((((a + 180) % 360) + 360) % 360) - 180;
const label = (id) => partById(id)?.label || id;
const lower = (id) => label(id).toLowerCase();
const reduced = () => { try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };
const store = {
  get(k) { try { return localStorage.getItem(k); } catch { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch { /* private mode: the choice lasts this page */ } },
};
const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
/**
 * Widths and positions ride in the markup as data-w and data-l (percent) and
 * are set here through the CSSOM, so the markup carries no style attribute and
 * keeps working if style-src ever drops 'unsafe-inline' (SECURITY.md, item 10).
 */
function applyStyles(scope) {
  for (const el of scope.querySelectorAll('[data-w], [data-l]')) {
    if (el.dataset.w) el.style.width = `${el.dataset.w}%`;
    if (el.dataset.l) el.style.left = `${el.dataset.l}%`;
  }
}
/** Status words and glyph classes: good | okay | work | rough | few | unseen | off. */
const WORD = { ...COPY.status, off: COPY.notInMode };
const GLOW = { good: 'good', okay: 'okay', work: 'work' };

/* ======================================================================= *
 *  THE VIEW MODEL                                                          *
 * ======================================================================= */
function normalise(data = {}) {
  const d = data && typeof data === 'object' ? data : {};
  return {
    side: d.side === 'left' ? 'left' : 'right',
    calibrated: Boolean(d.calibrated),
    sessions: Array.isArray(d.sessions) ? d.sessions : [],
    snaps: { last: d.snaps?.last || null, best: d.snaps?.best && typeof d.snaps.best === 'object' ? d.snaps.best : {} },
    modes: d.modes && typeof d.modes === 'object' ? d.modes : {},
    xp: d.xp || { total: 0, level: 1, into: 0, toNext: BODY.XP_LEVEL },
    streak: fin(d.streak) ? d.streak : 0,
    badges: Array.isArray(d.badges) ? d.badges.map((b) => (typeof b === 'string' ? b : b?.id)).filter(Boolean) : [],
    fresh: Array.isArray(d.fresh) ? d.fresh : [],
    bestGood: d.bestGood && typeof d.bestGood === 'object' ? d.bestGood : {},
    celebrate: Array.isArray(d.celebrate) ? d.celebrate : [],
    placement: d.placement || store.get(PLACEMENT_KEY) || null,
    loading: Boolean(d.loading), error: Boolean(d.error),
  };
}

/** A snapshot unpacked once (checked, decoded, graded), or null. The screen only reads it. */
const unpacked = new WeakMap();
function unpack(snap) {
  if (!snap || typeof snap !== 'object') return null;
  if (!unpacked.has(snap)) { let u = null; try { u = unpackSnapshot(snap); } catch { u = null; } unpacked.set(snap, u); }
  return unpacked.get(snap);
}
/** Unpacked stored readings of a snapshot, or null. */
const graded = (snap) => unpack(snap)?.graded || null;

const REASON_ORDER = ['needs-side', 'needs-front', 'too-far', 'no-rim', 'palm-edge', 'fingers-hidden', 'hand-small', 'no-hand', 'low-fps', 'no-jump', 'not-landed', 'head-square', 'joint-hidden'];

/** Every part's state for a mode ('shoot' | 'dribble' | 'footwork') and a range ('last' | 'lately'). */
function buildModel(d, mode, range) {
  const shoot = mode === 'shoot';
  const sessions = shoot ? d.sessions : (d.modes?.[mode]?.sessions || []);
  const sum = partSummary(sessions);
  const latest = sessions[sessions.length - 1] || null;
  const inMode = new Set(MODE_PARTS[mode] || PART_IDS);
  const lastG = shoot ? graded(d.snaps.last) : null;
  const parts = {};
  for (const p of PARTS) {
    const id = p.id;
    const st = { id, status: null, score: null, n: 0, total: 0, conf: 0, view: null, reason: null, readings: [], lowest: null, sum: sum.parts[id], rep: !shoot };
    parts[id] = st;
    if (!inMode.has(id)) { st.status = 'off'; continue; }
    const qual = sessions.filter((s) => s.parts?.[id]?.enough);
    const pool = range === 'lately' && qual.length ? qual.slice(-BODY.POOL_N) : latest ? [latest] : [];
    const rows = pool.map((s) => s.parts?.[id]).filter((r) => r?.n);
    const n = rows.reduce((a, r) => a + r.n, 0);
    st.n = n;
    st.total = pool.reduce((a, s) => a + (s.n || 0), 0);
    st.conf = n ? rows.reduce((a, r) => a + (r.conf || 0) * r.n, 0) / n : 0;
    const views = [...new Set(pool.map((s) => s.view).filter(Boolean))];
    st.view = views.length === 1 ? views[0] : views.length ? 'mixed' : null;
    const enough = range === 'lately' ? qual.length > 0 : Boolean(latest?.parts?.[id]?.enough);
    if (enough && n) { st.score = Math.round(rows.reduce((a, r) => a + r.score * r.n, 0) / n); st.status = statusOf(st.score); }
    // what was measured, reading by reading: the verdict average, else the last shot's rough read or its reason
    if (shoot) {
      for (const def of p.readings) {
        const k = `${id}.${def.key}`;
        const aggs = pool.map((s) => s.readings?.[k]).filter((a) => a?.n);
        if (aggs.length) {
          const an = aggs.reduce((a, x) => a + x.n, 0);
          const agg = { n: an, mean: aggs.reduce((a, x) => a + x.mean * x.n, 0) / an, score: Math.round(aggs.reduce((a, x) => a + x.score * x.n, 0) / an), x: aggs.reduce((a, x) => a + (x.x || 0), 0) };
          st.readings.push({ def, kind: 'verdict', agg, value: agg.mean, score: agg.score, status: statusOf(agg.score) });
        } else {
          const g = lastG?.[id]?.[def.key];
          if (g && g.value !== null && g.rough) st.readings.push({ def, kind: 'rough', value: g.value, extra: g.extra || 0 });
          else st.readings.push({ def, kind: 'unseen', reason: g?.reason || null });
        }
      }
      const verdicts = st.readings.filter((r) => r.kind === 'verdict');
      if (verdicts.length) st.lowest = verdicts.reduce((a, b) => (b.score < a.score ? b : a));
    }
    if (!st.status) {
      if ((latest?.parts?.[id]?.n || 0) > 0 || n > 0) st.status = 'few';
      else if (st.readings.some((r) => r.kind === 'rough')) st.status = 'rough';
      else {
        st.status = 'unseen';
        const rs = st.readings.map((r) => r.reason).filter(Boolean);
        st.reason = REASON_ORDER.find((r) => rs.includes(r)) || rs[0] || null;
      }
    }
  }
  // best part and next up (3.9): only parts with a status
  const rated = PART_IDS.filter((id) => GLOW[parts[id].status]).map((id) => ({ id, s: parts[id].score }));
  let best = null, next = null;
  if (rated.length) {
    best = rated.reduce((a, b) => (b.s > a.s ? b : a)).id;
    const low = rated.reduce((a, b) => (b.s < a.s ? b : a)).id;
    if (rated.length > 1 && low !== best) next = low;
  }
  const bestWhen = range === 'last' && latest?.day === todayLocal() ? 'today' : 'lately';
  return { mode, range, shoot, sessions, latest, parts, best, next, bestWhen, empty: !sessions.length };
}

/* ======================================================================= *
 *  SMALL PICTURES                                                          *
 * ======================================================================= */
const ICON = {
  check: '<path d="M-3.6 0.2 -1 2.8 3.8 -2.4" fill="none" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/>',
  dot: '<circle r="2.4"/>',
  up: '<path d="M0 3.6V-3.2M-3.1 -0.4 0 -3.6 3.1 -0.4" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>',
  cam: '<path d="M-4.2 -1.8h1.6l1-1.4h3.2l1 1.4h1.6v4.6h-8.4z" fill="none" stroke-width="1.4" stroke-linejoin="round"/><circle r="1.4" cy="0.5" fill="none" stroke-width="1.3"/>',
};
/** A status mark as a tiny standalone SVG, for the list, the legend and the sheet. */
function mark(status, size = 18) {
  const g = status === 'good' ? ICON.check : status === 'okay' ? ICON.dot : status === 'work' ? ICON.up : status === 'unseen' ? ICON.cam : '';
  return `<svg class="bm-mark s-${status}" viewBox="-10 -10 20 20" width="${size}" height="${size}" aria-hidden="true" focusable="false"><circle class="bm-mark-bg" r="8.6"/><g class="bm-mark-g">${g}</g></svg>`;
}
const FLAME = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path class="bm-flame" d="M12.3 2.5c.6 3.1-1.3 4.6-2.8 6.3-1.4 1.6-2.6 3.3-2.6 5.8 0 3.4 2.4 6.4 5.6 6.9-1.5-1-2.3-2.6-2.1-4.3.3-2.1 2.2-3.2 2.8-5.1 1.6 1.4 2.6 3.2 2.7 5.2.1 1.6-.6 3.1-1.7 4.1 3.1-.8 5.1-3.6 5.1-6.8 0-4.9-3.6-8.3-7-12.1z"/></svg>';
const MEDAL = (tier, locked) => `<svg class="bm-medal t-${tier}${locked ? ' locked' : ''}" viewBox="0 0 40 46" width="40" height="46" aria-hidden="true" focusable="false"><path class="bm-ribbon" d="M12 2h7l3 12h-7zM28 2h-7l-3 12h7z"/><circle class="bm-medal-o" cx="20" cy="27" r="15"/><circle class="bm-medal-i" cx="20" cy="27" r="10.5"/>${locked ? '<path class="bm-lock" d="M16 26v-2.4a4 4 0 0 1 8 0V26M14.8 26h10.4v7.2H14.8z"/>' : '<path class="bm-star" d="m20 20.6 1.9 3.9 4.3.6-3.1 3 .7 4.3-3.8-2-3.8 2 .7-4.3-3.1-3 4.3-.6z"/>'}</svg>`;

/* ======================================================================= *
 *  THE FIGURE (one static SVG string per render, BODY-MAP.md 3.3)          *
 * ======================================================================= */
const TORSO = 'M150 99Q180 93 210 99Q227 103 227 118L219 174L213 238H147L141 174L133 118Q133 103 150 99Z';
const JERSEY = 'M149 101H164Q180 126 196 101H211C213 124 216 138 223 150L215 238H145L137 150C144 138 147 124 149 101Z';
const SHORTS = 'M145 228H215L227 300Q207 307 186 301L180 274L174 301Q153 307 133 300Z';
const LIMBS = [
  // [path, width]: legs, guide arm, neck, shooting arm (drawn in this order)
  ['M163 252L158 330L156 410', 26],
  ['M197 252L202 330L204 410', 26],
  ['M222 116L240 166L238 204', 20],
  ['M180 76V100', 20],
  ['M139 116L118 92L120 54', 21],
];
function shoe(x, flip) {
  const s = flip ? -1 : 1;
  const p = (dx, dy) => `${x + s * dx} ${418 + dy}`;
  return `<path class="bm-f-shoe" d="M${p(-17, 2)}Q${p(-18, -6)} ${p(-6, -7)}L${p(8, -7)}Q${p(19, -6)} ${p(19, 4)}L${p(19, 18)}Q${p(19, 25)} ${p(10, 25)}L${p(-12, 25)}Q${p(-20, 25)} ${p(-20, 17)}Z"/>`
    + `<path class="bm-f-sole" d="M${p(-20, 17)}L${p(19, 17)}L${p(19, 19)}Q${p(19, 25)} ${p(10, 25)}L${p(-12, 25)}Q${p(-20, 25)} ${p(-20, 19)}Z"/>`
    + `<path class="bm-f-lace" d="M${p(-7, -1)}L${p(6, -1)}M${p(-8, 4)}L${p(7, 4)}"/>`;
}
function ball(cx, cy, r) {
  return `<circle class="bm-f-ball" cx="${cx}" cy="${cy}" r="${r}"/>`
    + `<path class="bm-f-seam" d="M${cx - r} ${cy}Q${cx} ${cy - r * 0.18} ${cx + r} ${cy}M${cx} ${cy - r}Q${cx - r * 0.18} ${cy} ${cx} ${cy + r}M${cx - r * 0.72} ${cy - r * 0.7}Q${cx - r * 0.3} ${cy} ${cx - r * 0.72} ${cy + r * 0.7}M${cx + r * 0.72} ${cy - r * 0.7}Q${cx + r * 0.3} ${cy} ${cx + r * 0.72} ${cy + r * 0.7}"/>`
    + `<circle class="bm-f-shine" cx="${cx - r * 0.38}" cy="${cy - r * 0.42}" r="${r * 0.22}"/>`;
}
function bodyArt() {
  const outline = LIMBS.map(([d, w]) => `<path d="${d}" stroke-width="${w + 7}"/>`).join('');
  const fillp = LIMBS.map(([d, w]) => `<path d="${d}" stroke-width="${w}"/>`).join('');
  return `<g class="bm-art" aria-hidden="true">
    <ellipse class="bm-f-shadow" cx="180" cy="446" rx="78" ry="10"/>
    <g class="bm-f-o">${outline}<path d="${TORSO}" stroke-width="7"/><circle cx="180" cy="54" r="28.5"/><circle cx="238" cy="210" r="11.5"/><ellipse cx="120" cy="47" rx="14" ry="10"/></g>
    <g class="bm-f-b">${fillp}<path class="bm-solid" d="${TORSO}"/><circle cx="180" cy="54" r="25"/><circle cx="238" cy="210" r="8"/><ellipse cx="120" cy="47" rx="10.5" ry="6.5"/></g>
    <path class="bm-f-shorts" d="${SHORTS}"/>
    <path class="bm-f-stripe" d="M139 236L131 298M221 236L229 298"/>
    <path class="bm-f-jersey" d="${JERSEY}"/>
    <path class="bm-f-trim" d="M164 101Q180 126 196 101M149 101C147 124 144 138 137 150M211 101C213 124 216 138 223 150"/>
    <rect class="bm-f-sock" x="146" y="398" width="20" height="12" rx="3"/><rect class="bm-f-sock" x="194" y="398" width="20" height="12" rx="3"/>
    ${shoe(152, false)}${shoe(208, true)}
    <clipPath id="bm-clip-head"><circle cx="180" cy="54" r="25"/></clipPath>
    <rect class="bm-f-band" x="150" y="37" width="60" height="9" clip-path="url(#bm-clip-head)"/>
    <circle class="bm-f-eye" cx="171.5" cy="56" r="2.6"/><circle class="bm-f-eye" cx="188.5" cy="56" r="2.6"/>
    <path class="bm-f-smile" d="M172.5 64.5Q180 70.5 187.5 64.5"/>
    ${ball(121, 26, 19)}
  </g>`;
}
/** The shooting hand in the bubble: wrist, palm, four spread fingers and a thumb, reaching up. */
const HAND = (() => {
  const W = [76, 94], d = [-0.35, -0.94], n = [0.94, -0.35];
  const at = (a, c) => [W[0] + d[0] * a + n[0] * c, W[1] + d[1] * a + n[1] * c];
  const rot = (t) => { const c = Math.cos(t * Math.PI / 180), s2 = Math.sin(t * Math.PI / 180); return [d[0] * c - d[1] * s2, d[0] * s2 + d[1] * c]; };
  const f = (off, deg, len) => { const k = at(31, off), r = rot(deg); return `M${k[0].toFixed(1)} ${k[1].toFixed(1)}L${(k[0] + r[0] * len).toFixed(1)} ${(k[1] + r[1] * len).toFixed(1)}`; };
  const t0 = at(11, -15), tr = rot(-52);
  const arm = `M${(W[0] - d[0] * 44).toFixed(1)} ${(W[1] - d[1] * 44).toFixed(1)}L${W[0]} ${W[1]}`;
  const palm = `M${W[0]} ${W[1]}L${at(27, 0).map((v) => v.toFixed(1)).join(' ')}`;
  const fingers = [f(-11.5, -13, 22), f(-3.9, -4, 26.5), f(3.9, 4, 24.5), f(11.5, 13, 18.5)];
  const thumb = `M${t0[0].toFixed(1)} ${t0[1].toFixed(1)}L${(t0[0] + tr[0] * 19).toFixed(1)} ${(t0[1] + tr[1] * 19).toFixed(1)}`;
  return { arm, palm, fingers, thumb };
})();
function handPaths() {
  const o = `<path d="${HAND.arm}" stroke-width="35"/><path d="${HAND.palm}" stroke-width="37"/>${HAND.fingers.map((p) => `<path d="${p}" stroke-width="15"/>`).join('')}<path d="${HAND.thumb}" stroke-width="15.5"/>`;
  const i = `<path d="${HAND.arm}" stroke-width="28"/><path d="${HAND.palm}" stroke-width="30"/>${HAND.fingers.map((p) => `<path d="${p}" stroke-width="8.5"/>`).join('')}<path d="${HAND.thumb}" stroke-width="9"/>`;
  return `<g class="bm-f-o">${o}</g><g class="bm-f-b">${i}</g>`;
}
function handBubble() {
  const { x, y, r } = BUBBLE.hand;
  return `<g class="bm-bubble" aria-hidden="true">
    <path class="bm-lead" d="M${x + r * 0.95} ${y - r * 0.2}L110 44"/>
    <circle class="bm-bub-bg" cx="${x}" cy="${y}" r="${r}"/>
    <clipPath id="bm-clip-hand"><circle cx="${x}" cy="${y}" r="${r - 2}"/></clipPath>
    <g clip-path="url(#bm-clip-hand)">
      ${handPaths()}
    </g>
    <circle class="bm-bub-rim" cx="${x}" cy="${y}" r="${r}"/>
  </g>`;
}
function footBubble() {
  const { x, y, r } = BUBBLE.foot;
  return `<g class="bm-bubble" aria-hidden="true">
    <path class="bm-lead" d="M${x - r * 0.98} ${y - r * 0.12}L228 440"/>
    <circle class="bm-bub-bg" cx="${x}" cy="${y}" r="${r}"/>
    <clipPath id="bm-clip-foot"><circle cx="${x}" cy="${y}" r="${r - 2}"/></clipPath>
    <g clip-path="url(#bm-clip-foot)">
      <g class="bm-f-o"><path d="M285 370V420" stroke-width="29"/></g><g class="bm-f-b"><path d="M285 370V420" stroke-width="22"/></g>
      <rect class="bm-f-sock" x="271" y="408" width="28" height="18" rx="4"/>
      <path class="bm-f-shoe" d="M264 428L302 426Q308 443 324 451Q348 459 352 476V488Q352 496 344 496H262Q254 496 254 488V452Q254 431 264 428Z"/>
      <path class="bm-f-sole" d="M254 484H352V488Q352 496 344 496H262Q254 496 254 488Z"/>
      <path class="bm-f-stripe2" d="M266 466Q296 458 334 469"/>
      <path class="bm-f-lace" d="M306 442L312 437M311 447L317 442M317 451L323 447"/>
    </g>
    <circle class="bm-bub-rim" cx="${x}" cy="${y}" r="${r}"/>
  </g>`;
}
/** Shapes for one hotspot, at a given inflation (the glow, the ring, the hit area). */
function spotShape(s, cls, grow = 0, extra = '') {
  if (s.kind === 'circle') return `<circle class="${cls}" cx="${s.x}" cy="${s.y}" r="${s.r + grow}"${extra}/>`;
  if (s.kind === 'pair') return s.pts.map(([x, y]) => `<circle class="${cls}" cx="${x}" cy="${y}" r="${s.r + grow}"${extra}/>`).join('');
  const w = s.w + grow * 2, h = s.h + grow * 2;
  const rx = s.kind === 'pill' ? h / 2 : 26 + grow;
  return `<rect class="${cls}" x="${s.x - w / 2}" y="${s.y - h / 2}" width="${w}" height="${h}" rx="${rx}"${extra}/>`;
}
function glowShape(s, status) {
  const f = ` fill="url(#bm-g-${status})"`;
  const g = s.bubble ? 2 : 6;
  if (s.kind === 'circle') return `<circle class="bm-glow" cx="${s.x}" cy="${s.y}" r="${s.r + g}"${f}/>`;
  if (s.kind === 'pair') return s.pts.map(([x, y]) => `<circle class="bm-glow" cx="${x}" cy="${y}" r="${s.r + g}"${f}/>`).join('');
  return `<ellipse class="bm-glow" cx="${s.x}" cy="${s.y}" rx="${s.w / 2 + g}" ry="${s.h / 2 + g}"${f}/>`;
}
function hitShape(s) {
  if (s.kind === 'circle') return `<circle class="bm-hit" cx="${s.x}" cy="${s.y}" r="${s.bubble ? s.r : Math.max(s.r, s.hit || 26)}"/>`;
  if (s.kind === 'pair') return s.pts.map(([x, y]) => `<circle class="bm-hit" cx="${x}" cy="${y}" r="${s.hit}"/>`).join('');
  const h = Math.max(s.h, s.hitH || s.h), w = s.w + 10;
  return `<rect class="bm-hit" x="${s.x - w / 2}" y="${s.y - h / 2}" width="${w}" height="${h}" rx="${s.kind === 'pill' ? h / 2 : 18}"/>`;
}
function glyphAt(s) {
  if (s.kind === 'circle') return [s.x + s.r * 0.74, s.y - s.r * 0.74];
  if (s.kind === 'pair') return [s.pts[1][0] + s.r * 0.8, s.pts[1][1] - s.r * 0.8];
  return [s.x + s.w / 2 - 4, s.y - s.h / 2 + 3];
}
function spotAria(st) {
  const w = WORD[st.status] || WORD.unseen;
  const score = GLOW[st.status] && fin(st.score) ? `, ${fill(COPY.scoreWord, { n: st.score })}` : '';
  return `${label(st.id)}: ${w}${score}. ${fill(COPY.openPart, { part: lower(st.id) })}`;
}
function figureSVG(model, side) {
  const left = side === 'left';
  const spots = FIG_ORDER.map((id) => {
    const s = SPOT[id], st = model.parts[id];
    const status = st.status;
    const [gx, gy] = glyphAt(s);
    const g = status === 'good' ? ICON.check : status === 'okay' ? ICON.dot : status === 'work' ? ICON.up : status === 'unseen' ? ICON.cam : '';
    const glyph = g ? `<g class="bm-glyph" transform="translate(${gx} ${gy})${left ? ' scale(-1 1)' : ''}"><circle class="bm-glyph-bg" r="8.5"/><g class="bm-mark-g">${g}</g></g>` : '';
    const glow = GLOW[status] ? glowShape(s, status) : '';
    return `<g class="bm-spot s-${status}${s.bubble ? ' in-bubble' : ''}" data-part="${id}" role="button" tabindex="0" aria-label="${esc(spotAria(st))}">`
      + `${glow}${spotShape(s, 'bm-ring', 0, ' pathLength="100"')}${spotShape(s, 'bm-focus', 5)}${glyph}${hitShape(s)}</g>`;
  }).join('');
  const grad = (id) => `<radialGradient id="bm-g-${id}"><stop offset="0" class="bm-st-${id}" stop-opacity=".78"/><stop offset=".55" class="bm-st-${id}" stop-opacity=".38"/><stop offset="1" class="bm-st-${id}" stop-opacity="0"/></radialGradient>`;
  const lab = (x, y, text) => `<text class="bm-bub-label" x="${left ? 360 - x : x}" y="${y}" text-anchor="middle">${esc(text)}</text>`;
  return `<svg class="bm-fig" viewBox="0 0 360 520" role="group" aria-label="${esc(COPY.figureAria)}">
    <defs>${grad('good')}${grad('okay')}${grad('work')}
      <linearGradient id="bm-jersey" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#FF5A1F"/><stop offset="1" stop-color="#FFA24D"/></linearGradient>
    </defs>
    <g class="bm-figure"${left ? ' transform="translate(360 0) scale(-1 1)"' : ''}>
      ${bodyArt()}${handBubble()}${footBubble()}${spots}
    </g>
    ${lab(58, 126, COPY.handBubble)}${lab(300, 390, COPY.footBubble)}
  </svg>`;
}

/* ======================================================================= *
 *  THE PAGE                                                                *
 * ======================================================================= */
function headHTML(d) {
  const xp = d.xp;
  const into = fin(xp.into) ? xp.into : (xp.total || 0) % BODY.XP_LEVEL;
  const pct = clamp(Math.round((100 * into) / BODY.XP_LEVEL), 0, 100);
  const streak = d.streak;
  const sLabel = streak === 1 ? COPY.streakOne : streak ? fill(COPY.streak, { n: streak }) : COPY.streakNone;
  return `<header class="bm-head">
    <div class="bm-title"><h1 id="bm-title">${esc(COPY.title)}</h1><p>${esc(COPY.sub)}</p></div>
    <div class="bm-stats">
      <div class="bm-xp" role="img" aria-label="${esc(fill(COPY.xpAria, { n: xp.level || 1, x: xp.toNext ?? BODY.XP_LEVEL }))}">
        <b>${esc(fill(COPY.level, { n: xp.level || 1 }))}</b>
        <span class="bm-xpbar"><i data-w="${pct}"></i></span>
        <small>${esc(fill(COPY.toNext, { n: xp.toNext ?? BODY.XP_LEVEL }))}</small>
      </div>
      <div class="bm-streak${streak ? ' on' : ''}" role="img" aria-label="${esc(sLabel)}" title="${esc(sLabel)}">${FLAME}<b>${streak}</b><small>${esc(streak ? (streak === 1 ? COPY.streakUnit.one : COPY.streakUnit.many) : sLabel)}</small></div>
    </div>
  </header>`;
}
function controlsHTML() {
  const tab = (m) => `<button type="button" role="tab" id="bm-tab-${m}" data-mode="${m}" aria-selected="${ui.mode === m}" aria-controls="bm-panel" tabindex="${ui.mode === m ? 0 : -1}">${esc(COPY.modes[m])}</button>`;
  return `<div class="bm-controls">
    <div class="bm-modes" role="tablist" aria-label="${esc(COPY.modeLabel)}">${tab('shoot')}${tab('dribble')}${tab('footwork')}</div>
    <label class="bm-range"><span class="bm-sr">${esc(COPY.rangeLabel)}</span>
      <select id="bm-range">${['last', 'lately'].map((r) => `<option value="${r}"${ui.range === r ? ' selected' : ''}>${esc(COPY.pick[r])}</option>`).join('')}</select>
    </label>
  </div>`;
}
function legendHTML() {
  return `<ul class="bm-legend" aria-label="${esc(COPY.legendAria)}">${COPY.legend.map(([s, w]) => `<li>${mark(s, 16)}<span>${esc(w)}</span></li>`).join('')}</ul>`;
}
function summaryHTML(m, d) {
  if (d.loading) return `<p class="bm-summary" role="status">${esc(COPY.loading)}</p>`;
  if (d.error) return `<p class="bm-summary bm-err" role="status">${esc(COPY.loadError)}</p>`;
  if (m.empty) return `<p class="bm-summary">${esc(m.shoot ? COPY.noData : COPY.modeNote[m.mode] || COPY.noData)}</p>`;
  const bits = [];
  if (m.best) bits.push(fill(COPY.best, { when: COPY.bestWhen[m.bestWhen], part: label(m.best) }));
  if (m.next) bits.push(fill(COPY.next, { part: label(m.next) }));
  if (!bits.length) bits.push(COPY.fewLine);
  return `<p class="bm-summary">${esc(bits.join(' '))}</p>`;
}
function rowHTML(m, id) {
  const st = m.parts[id];
  const tag = id === m.best ? `<span class="bm-tag t-best">${esc(COPY.tags.best)}</span>` : id === m.next ? `<span class="bm-tag t-next">${esc(COPY.tags.next)}</span>` : '';
  const arrow = st.sum?.arrow && GLOW[st.status] ? `<span class="bm-arrow a-${st.sum.arrow}" aria-hidden="true">${st.sum.arrow === 'up' ? '&#8599;' : '&#8600;'}</span>` : '';
  const score = GLOW[st.status] && fin(st.score) ? `<b class="bm-row-score">${st.score}</b>` : '';
  return `<li><button type="button" class="bm-row s-${st.status}" data-part="${id}" aria-label="${esc(spotAria(st))}"${ui.part === id && (ui.sheet || ui.layout === 'desk') ? ' aria-current="true"' : ''}>
    ${mark(st.status, 22)}<span class="bm-row-name">${esc(label(id))}${tag}</span><span class="bm-row-word">${esc(WORD[st.status])}</span>${arrow}${score}
  </button></li>`;
}
function setupHTML(d) {
  const pl = d.placement;
  const b = (v) => `<button type="button" class="bm-place${pl === v ? ' on' : ''}" data-place="${v}" aria-pressed="${pl === v}">${esc(COPY.setup[v])}</button>`;
  const unlock = pl === 'rim' ? COPY.setup.front : pl === 'side' ? COPY.setup.sideUnlocks : null;
  return `<section class="bm-setup${pl ? ' done' : ''}" aria-labelledby="bm-setup-h">
    <h2 id="bm-setup-h">${esc(COPY.setup.title)}</h2>
    <div class="bm-places">${b('rim')}${b('side')}${b('other')}</div>
    ${unlock ? `<p class="bm-unlock">${esc(unlock)}</p>` : `<p class="bm-unlock">${esc(COPY.setup.front)}</p><p class="bm-unlock">${esc(COPY.setup.sideUnlocks)}</p>`}
    <p class="bm-unlock bm-dim">${esc(COPY.setup.upright)}</p>
  </section>`;
}
function badgesHTML(d) {
  const ids = Object.keys(COPY.badges);
  const cards = ids.map((id) => {
    const b = COPY.badges[id];
    const has = d.badges.includes(id);
    const prog = !has && b.part ? `<span class="bm-prog"><i data-w="${clamp(Math.round((100 * (d.bestGood[b.part] || 0)) / BODY.BADGE_GOOD), 0, 100)}"></i></span>` : '';
    return `<li class="bm-badge${has ? ' has' : ''}">${MEDAL(b.tier, !has)}<div><b>${esc(b.name)}</b><small>${esc(has ? b.blurb : b.part ? fill(COPY.badgeProgress, { n: Math.min(BODY.BADGE_GOOD, d.bestGood[b.part] || 0), m: BODY.BADGE_GOOD }) : b.blurb)}</small>${prog}</div><span class="bm-sr">${esc(has ? COPY.badgeEarned : COPY.badgeLockedTag)}</span></li>`;
  }).join('');
  return `<section class="bm-badges" aria-labelledby="bm-badges-h"><h2 id="bm-badges-h">${esc(COPY.badgesTitle)}</h2><ul>${cards}</ul></section>`;
}
function pageHTML(d, m) {
  return `${headHTML(d)}${controlsHTML()}
  <div class="bm-grid" id="bm-panel" role="tabpanel" aria-labelledby="bm-tab-${ui.mode}">
    <section class="bm-figwrap" aria-label="${esc(COPY.figureAria)}">
      <div class="bm-figbox">${figureSVG(m, d.side)}<div class="bm-burst" aria-hidden="true"></div></div>
      ${legendHTML()}
    </section>
    <section class="bm-side" aria-labelledby="bm-parts-h">
      ${summaryHTML(m, d)}
      <h2 class="bm-h2" id="bm-parts-h">${esc(COPY.partsTitle)}</h2>
      <ul class="bm-list">${PART_IDS.map((id) => rowHTML(m, id)).join('')}</ul>
    </section>
    <div class="bm-sheet" id="bm-sheet" aria-labelledby="bm-sh-h" hidden></div>
  </div>
  <div class="bm-scrim" hidden></div>
  ${setupHTML(d)}${badgesHTML(d)}
  <div class="bm-chip" role="status" aria-live="polite"></div>`;
}

/* ======================================================================= *
 *  RENDER                                                                  *
 * ======================================================================= */
/** Draw the whole screen. Idempotent: the mode, range and open part carry over. */
export function render(container, data) {
  if (!container) return;
  const t0 = performance.now();
  dropReplay();
  if (root && root !== container) detach();
  root = container;
  D = normalise(data);
  M = buildModel(D, ui.mode, ui.range);
  let host = root.querySelector(':scope > .bm');
  if (!host) { root.innerHTML = '<div class="bm"></div>'; host = root.firstElementChild; }
  host.dataset.side = D.side;
  host.dataset.mode = ui.mode;
  setLayout(host, measureLayout(root));
  host.innerHTML = pageHTML(D, M);
  applyStyles(host);
  if (!root.__bm) { bind(root); root.__bm = true; }
  if (!ro && typeof ResizeObserver === 'function') {
    ro = new ResizeObserver(() => { const h = root?.querySelector('.bm'); if (h) relayout(h); });
    ro.observe(root);
  }
  // Desktop always shows a part once there is data ("Next up" until the
  // player picks one); the phone and tablet sheet stays as it was.
  const ready = !D.loading && !D.error;
  if (ui.layout === 'desk') { if (ready) { if (!ui.picked) ui.part = M.next || M.best || 'elbow'; paintSheet(false); } }
  else if (ui.sheet && ui.part) paintSheet(false);
  stats.renderMs = performance.now() - t0;
  // celebrations the Worker says are new, once each, after the first paint
  for (const c of D.celebrate) if (c?.part && !played.has(c.key || `${c.part}:${c.to}`)) queue.push(c);
  // New badges: one chip each, or one chip for them all when there are three
  // or more (a first visit after a few sessions can bring a pile of them).
  const fresh = D.fresh.filter((id) => COPY.badges[id] && !played.has(`badge:${id}`));
  if (fresh.length > 2) queue.push({ badges: fresh });
  else for (const id of fresh) queue.push({ badge: id });
  if (queue.length && !celebrating) setTimeout(nextCelebration, 450);
}

function detach() {
  if (ro) { ro.disconnect(); ro = null; }
}
function measureLayout(el) {
  const w = el.clientWidth || (typeof innerWidth === 'number' ? innerWidth : 1024);
  return w >= LAYOUT.desk ? 'desk' : w >= LAYOUT.tablet ? 'tablet' : 'phone';
}
function setLayout(host, layout) {
  ui.layout = layout;
  host.dataset.layout = layout;
}
function relayout(host) {
  const next = measureLayout(root);
  if (next === ui.layout) return;
  const was = ui.layout;
  setLayout(host, next);
  const sheet = host.querySelector('.bm-sheet');
  if (next === 'desk') { if (!ui.picked) ui.part = M?.next || M?.best || 'elbow'; ui.sheet = false; hideScrim(); if (!D?.loading && !D?.error) paintSheet(false); }
  else if (was === 'desk') { ui.sheet = false; dropReplay(); if (sheet) { sheet.hidden = true; sheet.classList.remove('on'); } hideScrim(); }
  else if (ui.sheet) { sheetRole(sheet); if (next === 'phone') showScrim(); else hideScrim(); }
  markCurrent();
}

/* ------------------------------------------------------------ events */
function bind(el) {
  el.addEventListener('click', onClick);
  el.addEventListener('keydown', onKey);
  el.addEventListener('change', onChange);
}
function onClick(e) {
  const t = e.target;
  const spot = t.closest?.('.bm-spot, .bm-row');
  if (spot && root.contains(spot)) { ui.opener = spot; open(spot.dataset.part, { focus: e.detail === 0 }); return; }
  // role="tab" only: the .bm host carries data-mode too, and would swallow every other click
  const tab = t.closest?.('[role="tab"][data-mode]');
  if (tab) { setMode(tab.dataset.mode); return; }
  if (t.closest?.('[data-act="close"]') || t.closest?.('.bm-scrim')) { close(); return; }
  const place = t.closest?.('[data-place]');
  if (place) { setPlacement(place.dataset.place); return; }
  const drill = t.closest?.('[data-act="drill"]');
  if (drill) {
    root.dispatchEvent(new CustomEvent('bodymap:drill', { bubbles: true, detail: { part: drill.dataset.part, skill: drill.dataset.skill } }));
    return;
  }
  const shot = t.closest?.('[data-shot]');
  if (shot && !shot.disabled) { ui.shot = shot.dataset.shot; paintSheet(false, true); return; }
  const chip = t.closest?.('.bm-chip');
  if (chip?.dataset.part) open(chip.dataset.part, { focus: true });
  else if (chip?.dataset.go === 'badges') root.querySelector('.bm-badges')?.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
}
function onKey(e) {
  const t = e.target;
  if (t.classList?.contains('bm-spot') && (e.key === 'Enter' || e.key === ' ')) {
    e.preventDefault(); ui.opener = t; open(t.dataset.part, { focus: true }); return;
  }
  if (t.classList?.contains('bm-spot') && e.key.startsWith('Arrow')) { e.preventDefault(); moveSpot(t, e.key); return; }
  if (t.getAttribute?.('role') === 'tab' && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
    e.preventDefault();
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const i = tabs.indexOf(t), n = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    setMode(n.dataset.mode); root.querySelector(`#bm-tab-${n.dataset.mode}`)?.focus();
    return;
  }
  if (e.key === 'Escape' && (ui.sheet || ui.layout === 'desk') && root.querySelector('.bm-sheet')?.contains(t)) {
    e.preventDefault(); if (ui.layout === 'desk') ui.opener?.focus?.(); else close(); return;
  }
  if (e.key === 'Tab' && ui.sheet && ui.layout === 'phone') trapFocus(e);
}
function onChange(e) {
  if (e.target.id === 'bm-range') { ui.range = e.target.value === 'lately' ? 'lately' : 'last'; rerender(); }
}
/** Escape pressed anywhere while the phone or tablet sheet is open. */
function onDocKey(e) {
  if (e.key === 'Escape' && ui.sheet && ui.layout !== 'desk') { e.preventDefault(); close(); }
}
function rerender() { if (root && D) render(root, rawOf(D)); }
/** render() takes the Worker's shape; D is already normalised, which render() accepts as is. */
const rawOf = (d) => ({ ...d, celebrate: [], fresh: [] });
function setMode(mode) {
  if (!COPY.modes[mode] || mode === ui.mode) return;
  ui.mode = mode;
  rerender();
  root.querySelector(`#bm-tab-${mode}`)?.focus();
}
function setPlacement(p) {
  if (!['rim', 'side', 'other'].includes(p)) return;
  store.set(PLACEMENT_KEY, p);
  if (D) D.placement = p;
  const host = root.querySelector('.bm-setup');
  if (host) host.outerHTML = setupHTML(D);
  root.querySelector(`[data-place="${p}"]`)?.focus();
  root.dispatchEvent(new CustomEvent('bodymap:placement', { bubbles: true, detail: { placement: p } }));
}
/** Arrow keys on the figure: the nearest part in that direction, as drawn. */
function moveSpot(from, key) {
  const left = D?.side === 'left';
  const at = (id) => { const s = SPOT[id]; const x = s.kind === 'pair' ? 180 : s.x, y = s.kind === 'pair' ? s.pts[0][1] : s.y; return { x: left ? 360 - x : x, y }; };
  const a = at(from.dataset.part);
  const dir = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] }[key];
  if (!dir) return;
  let best = null, bs = Infinity;
  for (const id of PART_IDS) {
    if (id === from.dataset.part) continue;
    const b = at(id), dx = b.x - a.x, dy = b.y - a.y;
    const along = dx * dir[0] + dy * dir[1];
    if (along <= 4) continue;
    const across = Math.abs(dx * dir[1] - dy * dir[0]);
    const score = along + across * 2.2;
    if (score < bs) { bs = score; best = id; }
  }
  if (best) root.querySelector(`.bm-spot[data-part="${best}"]`)?.focus();
}
function trapFocus(e) {
  const sheet = root.querySelector('.bm-sheet');
  const els = [...sheet.querySelectorAll('button:not([disabled]), input, select, [tabindex="0"], [tabindex="-1"]#bm-sh-h')].filter((x) => x.offsetParent !== null || x === document.activeElement);
  if (!els.length) return;
  const first = els[0], last = els[els.length - 1];
  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
}

/* ======================================================================= *
 *  THE PART SHEET (BODY-MAP.md 3.4)                                        *
 * ======================================================================= */
/**
 * Open one part. On a phone the sheet slides up over the screen (a modal
 * dialog); on a tablet it opens over the list; on a desktop the detail column
 * switches to it. Returns close().
 */
export function open(partId, { focus = true } = {}) {
  if (!root || !D || !PART_IDS.includes(partId)) return () => {};
  const t0 = performance.now();
  if (ui.part !== partId) ui.shot = 'last';
  ui.part = partId;
  ui.picked = true;
  if (ui.layout !== 'desk') ui.sheet = true;
  // a celebration chip would sit on top of the sheet's headline on a phone
  if (ui.layout === 'phone') root.querySelector('.bm-chip')?.classList.remove('on');
  paintSheet(focus);
  stats.openMs = performance.now() - t0;
  return close;
}

/** Close the phone or tablet sheet and give focus back to what opened it. */
export function close() {
  if (!root) return;
  const sheet = root.querySelector('.bm-sheet');
  if (ui.layout === 'desk' || !ui.sheet || !sheet) return;
  ui.sheet = false;
  dropReplay();
  document.removeEventListener('keydown', onDocKey, true);
  sheet.classList.remove('on');
  hideScrim();
  const done = () => { if (!ui.sheet) sheet.hidden = true; };
  if (reduced()) done(); else setTimeout(done, 260);
  markCurrent();
  const back = ui.opener && root.contains(ui.opener) ? ui.opener : root.querySelector(`.bm-row[data-part="${ui.part}"]`);
  back?.focus?.({ preventScroll: true });
}

function sheetRole(sheet) {
  if (ui.layout === 'phone') { sheet.setAttribute('role', 'dialog'); sheet.setAttribute('aria-modal', 'true'); }
  else { sheet.setAttribute('role', 'region'); sheet.removeAttribute('aria-modal'); }
}
function showScrim() { const s = root.querySelector('.bm-scrim'); if (s) { s.hidden = false; requestAnimationFrame(() => s.classList.add('on')); } }
function hideScrim() { const s = root?.querySelector('.bm-scrim'); if (s) { s.classList.remove('on'); s.hidden = true; } }
function markCurrent() {
  root?.querySelectorAll('.bm-row, .bm-spot').forEach((el) => {
    const on = el.dataset.part === ui.part && (ui.sheet || ui.layout === 'desk');
    if (on) el.setAttribute('aria-current', 'true'); else el.removeAttribute('aria-current');
    el.classList.toggle('cur', on);
  });
}

/** Build the sheet for ui.part and show it. keepScroll: a shot switch inside the sheet. */
function paintSheet(focus, keepScroll = false) {
  const sheet = root?.querySelector('.bm-sheet');
  if (!sheet || !ui.part || !M) return;
  const scroll = keepScroll ? sheet.scrollTop : 0;
  dropReplay();
  sheet.dataset.part = ui.part;
  sheetRole(sheet);
  sheet.innerHTML = sheetHTML(ui.part);
  applyStyles(sheet);
  sheet.hidden = false;
  // The phone sheet slides up by a CSS animation that starts as it appears, so
  // no forced layout is needed to set up a transition.
  if (ui.layout === 'phone' && !sheet.classList.contains('on')) showScrim();
  sheet.classList.add('on');
  if (ui.layout !== 'desk') document.addEventListener('keydown', onDocKey, true);
  const sec = sheet.querySelector('.bm-replay');
  if (sec) {
    // The canvas is built just after the sheet first paints, so opening a part
    // never waits on it; the stage keeps its square size meanwhile, so nothing moves.
    const token = replayToken;
    requestAnimationFrame(() => setTimeout(() => { if (token === replayToken && sec.isConnected) replay = createReplay(sec, replayInput(ui.part)); }, 0));
  }
  sheet.scrollTop = scroll;
  markCurrent();
  if (focus || (ui.layout === 'phone' && ui.sheet && !sheet.contains(document.activeElement))) sheet.querySelector('#bm-sh-h')?.focus({ preventScroll: true });
}

function replayInput(id) {
  const snap = ui.shot === 'best' ? D.snaps.best[id] || null : D.snaps.last;
  return { snap, part: id, side: snap?.side || D.side };
}

function sheetHTML(id) {
  const st = M.parts[id];
  const pc = COPY.parts[id];
  const status = st.status;
  let head;
  if (st.rep) head = GLOW[status] ? { headline: fill(COPY.repHeadline, { part: lower(id), status: WORD[status].toLowerCase() }), tip: null } : headlineFor(id, status === 'off' ? 'unseen' : status);
  else head = headlineFor(id, status, st.lowest ? { key: st.lowest.def.key, value: st.lowest.value } : null);
  // How sure is worked out for shot readings only: a rep's score is the movement
  // layer's, and the Body Map does not claim a sureness it did not compute.
  const sure = GLOW[status] && !st.rep ? fill(COPY.sureLine, { sure: sureWord(st.conf) }) : null;
  const from = GLOW[status] || status === 'few'
    ? (st.rep ? fill(COPY.repLine, { n: st.n }) : fill(COPY.fromLine, { n: st.n, total: st.total, view: COPY.viewWords[st.view] || COPY.viewWords.mixed }))
    : null;
  const why = status === 'unseen' ? (st.reason ? reasonLine(st.reason, id) : st.rep ? COPY.modeNote[M.mode] : COPY.noData)
    : status === 'few' ? COPY.fewLine : status === 'off' ? COPY.modeNote[M.mode] : null;
  const close = ui.layout === 'desk' ? '' : `<button type="button" class="bm-x" data-act="close" aria-label="${esc(COPY.close)}"><svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg></button>`;
  return `<div class="bm-sheet-in">
    <div class="bm-sheet-bar"><span class="bm-grab" aria-hidden="true"></span><span class="bm-sheet-part">${esc(label(id))}</span>${close}</div>
    <header class="bm-sh-head s-${status}">
      <div class="bm-sh-title">${mark(status, 34)}<h2 id="bm-sh-h" tabindex="-1">${esc(head.headline)}</h2></div>
      <div class="bm-sh-meta"><span class="bm-pill s-${status}">${esc(WORD[status])}</span>${fin(st.score) && GLOW[status] ? `<span class="bm-sh-score">${st.score}<small>/100</small></span>` : ''}${sure ? `<span class="bm-sure">${esc(sure)}</span>` : ''}</div>
      ${from ? `<p class="bm-from">${esc(from)}</p>` : ''}
      ${why ? `<p class="bm-why">${esc(why)}</p>` : ''}
      ${head.tip ? `<p class="bm-tip"><b>${esc(COPY.tryThis)}</b> ${esc(head.tip)}</p>` : ''}
      ${pc.note && !st.rep ? `<p class="bm-note">${esc(pc.note)}</p>` : ''}
    </header>
    ${st.rep ? '' : readingsHTML(st)}
    ${st.rep ? `<section class="bm-sec"><h3>${esc(COPY.replayTitle)}</h3><p class="bm-dim">${esc(COPY.replayTitleRep)}</p></section>` : replayHTML(id)}
    ${trendHTML(id, st)}
    ${st.rep ? '' : `${drillHTML(id)}${partBadgeHTML(id)}`}
    <p class="bm-foot">${esc(COPY.footnote)}${D.calibrated ? '' : ` <span class="bm-rough">${esc(COPY.rough)}</span>`}</p>
  </div>`;
}

function readingsHTML(st) {
  const rows = st.readings.map((r) => {
    const def = r.def, part = st.id;
    if (r.kind === 'unseen') {
      return `<li class="bm-read s-unseen"><div class="bm-read-top">${mark('unseen', 18)}<b>${esc(def.label)}</b><span class="bm-read-word">${esc(COPY.status.unseen)}</span></div><p>${esc(r.reason ? reasonLine(r.reason, part) : COPY.fewLine)}</p></li>`;
    }
    const rough = r.kind === 'rough';
    const line = rough ? measuredLine(part, def.key, r.value, r.extra) : sessionLine(part, def.key, r.agg);
    const s = rough ? 'rough' : r.status;
    const note = rough ? COPY.lastShotRow : fill(COPY.avgOf, { n: r.agg.n });
    return `<li class="bm-read s-${s}"><div class="bm-read-top">${mark(s, 18)}<b>${esc(def.label)}</b><span class="bm-read-word">${esc(rough ? COPY.roughRow : WORD[s])}</span></div>
      <p>${esc(line)} <small>${esc(note)}</small></p>${bandHTML(part, def.key, r.value, s)}</li>`;
  }).join('');
  return `<section class="bm-sec"><h3>${esc(COPY.measuredTitle)}</h3><ul class="bm-reads">${rows}</ul></section>`;
}
/** The target band as a small bar with the player's dot on it. */
function bandHTML(part, key, value, s) {
  const b = bandsOf(part, key);
  if (!b || !fin(value)) return '';
  const [lo, hi] = b.domain;
  const x = (v) => clamp(((v - lo) / (hi - lo)) * 100, 0, 100);
  const v = partById(part).readings.find((r) => r.key === key)?.abs ? Math.abs(value) : value;
  const seg = (a) => { const l = x(Math.max(a[0], lo)), r = x(Math.min(a[1], hi)); return `data-l="${l.toFixed(1)}" data-w="${Math.max(0, r - l).toFixed(1)}"`; };
  const off = v < lo ? ' lo' : v > hi ? ' hi' : '';
  return `<div class="bm-band" aria-hidden="true"><span class="bm-band-okay" ${seg(b.okay)}></span><span class="bm-band-good" ${seg(b.good)}></span><i class="bm-band-dot s-${s}${off}" data-l="${x(v).toFixed(1)}"></i></div>`;
}

function replayHTML(id) {
  const has = { last: Boolean(D.snaps.last), best: Boolean(D.snaps.best[id]) };
  if (!has.last && !has.best) return `<section class="bm-sec bm-replay-none"><h3>${esc(COPY.replayTitle)}</h3><p class="bm-dim">${esc(COPY.replay.none)}</p></section>`;
  if (!has[ui.shot]) ui.shot = has.last ? 'last' : 'best';
  const R = COPY.replay, C = COPY.replayControls;
  const seg = ['last', 'best'].map((k) => `<button type="button" data-shot="${k}" aria-pressed="${ui.shot === k}"${has[k] ? '' : ' disabled'}>${esc(R[k])}</button>`).join('');
  const hand = id === 'fingers' || id === 'wrist';
  return `<section class="bm-sec bm-replay" aria-labelledby="bm-rp-h">
    <div class="bm-sec-h"><h3 id="bm-rp-h">${esc(COPY.replayTitle)}</h3><div class="bm-seg" role="group" aria-label="${esc(C.shotPick)}">${seg}</div></div>
    <div class="bm-stage">
      <canvas class="bm-cv" role="img" aria-label="${esc(R.none)}"></canvas>
      <span class="bm-mtag" aria-hidden="true"></span>
      <span class="bm-depth" aria-hidden="true">${esc(R.depth)}</span>
    </div>
    ${hand ? `<div class="bm-handrow"><canvas class="bm-hand" width="160" height="160" role="img" aria-label="${esc(R.hand)}"></canvas><p class="bm-handnote"></p></div>` : ''}
    <div class="bm-scrubrow">
      <button type="button" class="bm-play" aria-label="${esc(R.play)}"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path class="bm-i-play" d="M8 5.5v13l10.5-6.5z"/><path class="bm-i-pause" d="M7.5 5.5h3.4v13H7.5zM13.1 5.5h3.4v13h-3.4z"/></svg></button>
      <div class="bm-scrubwrap"><input type="range" class="bm-scrub" min="0" max="5" step="0.01" value="0" aria-label="${esc(R.scrub)}"><div class="bm-ticks" aria-hidden="true"></div></div>
    </div>
    <div class="bm-turns" role="group" aria-label="${esc(C.views)}">
      <span class="bm-turn-g"><button type="button" data-turn="left">${esc(R.turnLeft)}</button><button type="button" data-turn="right">${esc(R.turnRight)}</button></span>
      <span class="bm-turn-g"><button type="button" data-preset="camera">${esc(R.camera)}</button><button type="button" data-preset="side">${esc(R.side)}</button><button type="button" data-preset="top">${esc(R.top)}</button></span>
    </div>
    <p class="bm-drag bm-dim">${esc(R.drag)} ${esc(R.ideal)}</p>
  </section>`;
}

function trendHTML(id, st) {
  const trend = st.sum?.trend || [];
  let body;
  if (trend.length < 2) body = `<p class="bm-dim">${esc(COPY.trendEmpty)}</p>`;
  else {
    const dir = st.sum.arrow === 'up' ? COPY.trendDir.up : st.sum.arrow === 'down' ? COPY.trendDir.down : COPY.trendDir.flat;
    const aria = fill(COPY.trendAria, { part: label(id), n: trend.length, list: trend.map((t) => t.score).join(', '), dir });
    body = trendSVG(trend, aria);
  }
  return `<section class="bm-sec bm-trend"><h3>${esc(COPY.trendTitle)}</h3>${body}</section>`;
}
/** 240 x 64: this part's session score over its last qualifying sessions (3.6). */
function trendSVG(trend, aria) {
  const W = 240, H = 64, L = 8, R = 40, T = 6, B = 6;
  const y = (s) => T + ((100 - s) / 100) * (H - T - B);
  const x = (i) => L + (trend.length === 1 ? 0 : (i / (trend.length - 1)) * (W - L - R));
  const band = (a, b, c) => `<rect class="bm-tb-${c}" x="0" y="${y(b)}" width="${W}" height="${y(a) - y(b)}"/>`;
  const pts = trend.map((t, i) => `${x(i).toFixed(1)},${y(t.score).toFixed(1)}`).join(' ');
  const last = trend[trend.length - 1];
  const dots = trend.map((t, i) => `<circle class="bm-td s-${t.status}" cx="${x(i).toFixed(1)}" cy="${y(t.score).toFixed(1)}" r="${i === trend.length - 1 ? 4.2 : 3.2}"/>`).join('');
  return `<svg class="bm-tchart" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(aria)}">
    ${band(0, 39, 'work')}${band(40, 69, 'okay')}${band(70, 100, 'good')}
    <polyline class="bm-tl" points="${pts}"/>${dots}
    <text class="bm-tlab" x="${(x(trend.length - 1) + 8).toFixed(1)}" y="${(y(last.score) + 4).toFixed(1)}">${last.score}</text>
  </svg>`;
}
function drillHTML(id) {
  const d = COPY.drills[id];
  if (!d) return '';
  return `<section class="bm-sec bm-drill"><h3>${esc(COPY.drillTitle)}</h3>
    <div class="bm-drill-card"><b>${esc(d.name)}</b>${d.draft ? `<span class="bm-tag t-new">${esc(COPY.draftTag)}</span>` : ''}<p>${esc(d.how)}</p>
      <button type="button" class="bm-drill-go" data-act="drill" data-part="${id}" data-skill="${esc(d.skill)}">${esc(COPY.drillButton)}</button>
      <p class="bm-safety">${esc(SAFETY)}</p></div></section>`;
}
function partBadgeHTML(id) {
  const bid = PART_BADGE[id]; const b = COPY.badges[bid];
  if (!b) return '';
  const has = D.badges.includes(bid);
  const best = D.bestGood[id] || 0;
  return `<section class="bm-sec bm-pbadge"><h3>${esc(COPY.partBadgeTitle)}</h3>
    <div class="bm-badge${has ? ' has' : ''}">${MEDAL(b.tier, !has)}<div><b>${esc(b.name)}</b><small>${esc(has ? b.blurb : fill(COPY.badgeLocked, { part: lower(id), best }))}</small>${has ? '' : `<span class="bm-prog"><i data-w="${clamp(Math.round((100 * best) / BODY.BADGE_GOOD), 0, 100)}"></i></span>`}</div></div>
  </section>`;
}

/* ======================================================================= *
 *  THE REPLAY (BODY-MAP.md 3.5)                                            *
 *  Their own skeleton at the key frames, from world landmarks, projected   *
 *  by hand: yaw, tilt, then a pinhole 3.5 m back. Draws only on demand.    *
 * ======================================================================= */
const HEAD_R = 0.11;          // metres, the head drawn as a ball around the ears
function highlightFor(part, J) {
  switch (part) {
    case 'fingers': return { bones: [[J.W, J.index]], joints: [J.index] };
    case 'wrist': return { bones: [[J.E, J.W], [J.W, J.index]], joints: [J.W] };
    case 'elbow': return { bones: [[J.S, J.E], [J.E, J.W]], joints: [J.E] };
    case 'shoulders': return { bones: [[11, 12]], joints: [11, 12] };
    case 'head': return { bones: [], joints: [], head: true };
    case 'core': return { bones: [[11, 23], [12, 24], [11, 12], [23, 24]], joints: [], spine: true };
    case 'hips': return { bones: [[23, 24]], joints: [23, 24] };
    case 'knees': return { bones: [[23, 25], [25, 27], [24, 26], [26, 28]], joints: [25, 26] };
    case 'ankles': return { bones: [[25, 27], [26, 28], [27, 31], [28, 32]], joints: [27, 28] };
    case 'heels': return { bones: [[27, 29], [29, 31], [28, 30], [30, 32]], joints: [29, 30] };
    case 'toes': return { bones: [[27, 31], [29, 31], [28, 32], [30, 32]], joints: [31, 32] };
    default: return { bones: [], joints: [] };
  }
}

function createReplay(sec, { snap, part, side }) {
  const cv = sec.querySelector('.bm-cv');
  const g2 = cv.getContext('2d');
  const hcv = sec.querySelector('.bm-hand');
  const scrub = sec.querySelector('.bm-scrub'), playBtn = sec.querySelector('.bm-play');
  const depthEl = sec.querySelector('.bm-depth'), tagEl = sec.querySelector('.bm-mtag');
  const R = COPY.replay, C = COPY.replayControls;
  const U = unpack(snap);
  const moments = U ? MOMENTS.filter((k) => U.frames[k]) : [];
  if (!moments.length) {
    sec.querySelector('.bm-stage').insertAdjacentHTML('beforeend', `<p class="bm-empty">${esc(R.none)}</p>`);
    return { destroy() {} };
  }
  const J = sidePoints(side);
  const aspect = U.aspect || 16 / 9;
  const world = moments.every((k) => U.frames[k].w);
  const anchorK = U.frames.load ? 'load' : moments[0];
  const A = U.frames[anchorK];
  const img = (f, i) => ({ x: f.p[i * 3] * aspect, y: f.p[i * 3 + 1] });
  const hmid = (f) => { const a = img(f, 23), b = img(f, 24); return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; };
  const smid = (f) => { const a = img(f, 11), b = img(f, 12); return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; };
  const torsoPx = Math.hypot(smid(A).x - hmid(A).x, smid(A).y - hmid(A).y) || 0.2;
  const W3 = (f, i) => ({ x: f.w[i * 3], y: f.w[i * 3 + 1], z: f.w[i * 3 + 2] });
  let torsoW = 0.5;
  if (world) {
    const s = [W3(A, 11), W3(A, 12)], h = [W3(A, 23), W3(A, 24)];
    torsoW = Math.hypot((s[0].x + s[1].x - h[0].x - h[1].x) / 2, (s[0].y + s[1].y - h[0].y - h[1].y) / 2, (s[0].z + s[1].z - h[0].z - h[1].z) / 2) || 0.5;
  }
  const m = torsoW / torsoPx;                         // metres per square picture unit, for this body
  const h0 = hmid(A);
  // Every key frame as 33 x (x, y, z, vis): world points plus the root motion the picture shows
  const P = {};
  for (const k of moments) {
    const f = U.frames[k], out = new Float32Array(33 * 4).fill(NaN);
    const h = hmid(f), rx = (h.x - h0.x) * m, ry = (h.y - h0.y) * m;
    for (const i of KEEP_POINTS) {
      if (world) { out[i * 4] = f.w[i * 3] + rx; out[i * 4 + 1] = f.w[i * 3 + 1] + ry; out[i * 4 + 2] = f.w[i * 3 + 2]; }
      else { out[i * 4] = (f.p[i * 3] * aspect - h0.x) * m; out[i * 4 + 1] = (f.p[i * 3 + 1] - h0.y) * m; out[i * 4 + 2] = 0; }
      out[i * 4 + 3] = f.p[i * 3 + 2];
    }
    P[k] = out;
  }
  // Floor height and the body's forward direction (toward the rim), from the anchor
  const pa = P[anchorK];
  // The floor under the feet at the dip, and the top of the head. The numbers
  // are fallbacks for feet or a head the camera did not see, not limits: a
  // kid's feet sit well under 0.9 m below the hips.
  const feetY = [29, 30, 31, 32].map((i) => pa[i * 4 + 1]).filter(fin);
  const headY = [0, 7, 8, 11, 12].map((i) => pa[i * 4 + 1]).filter(fin);
  const floorY = (feetY.length ? Math.max(...feetY) : 0.9) + 0.01;
  const topY = (headY.length ? Math.min(...headY) : -0.7) - HEAD_R;
  // The whole shot has to fit as the phone saw it (yaw 0, tilt 0, with
  // perspective): the jump and the raised arm reach well above the dip, and
  // the hand nearer the lens looks bigger and higher than it would flat.
  const fitBox = { u0: Infinity, u1: -Infinity, v0: Infinity, v1: -Infinity };
  const take = (x, y, z) => {
    const d = CAM_D + (fin(z) ? z : 0);
    if (!(d > 0.5)) return;
    const uu = x / d, vv = y / d;
    if (uu < fitBox.u0) fitBox.u0 = uu; if (uu > fitBox.u1) fitBox.u1 = uu;
    if (vv < fitBox.v0) fitBox.v0 = vv; if (vv > fitBox.v1) fitBox.v1 = vv;
  };
  for (const k of moments) for (const i of KEEP_POINTS) {
    const x = P[k][i * 4], y = P[k][i * 4 + 1], z = P[k][i * 4 + 2];
    if (!fin(x) || !fin(y)) continue;
    take(x, y, z);
    if (i === 0 || i === 7 || i === 8) take(x, y - HEAD_R, z);
  }
  take(0, floorY, 0);
  if (!fin(fitBox.u0)) Object.assign(fitBox, { u0: -0.1, u1: 0.1, v0: -0.3, v1: 0.3 });
  let fwd = null;
  const base = U.frames.set || U.frames.release || A;
  const v = detectView(base, aspect);
  if (world) {
    const q = (i) => W3(base, i);
    const u = norm({ x: (q(11).x + q(12).x - q(23).x - q(24).x) / 2, y: (q(11).y + q(12).y - q(23).y - q(24).y) / 2, z: (q(11).z + q(12).z - q(23).z - q(24).z) / 2 });
    let r = sub(q(12), q(11)); r = norm(sub(r, scale(u, dot(r, u))));
    const f = cross(u, r);
    const h = Math.hypot(f.x, f.z);
    if (h > 0.2) fwd = { x: f.x / h, y: 0, z: f.z / h };
  } else if (v?.facing) fwd = { x: v.facing, y: 0, z: 0 };
  const facing = v?.facing || 0;
  const psiF = fwd ? Math.atan2(fwd.x, fwd.z) : 0;
  const maxU = moments.length - 1;
  let u = clamp(moments.indexOf(KEY_MOMENT[part]) >= 0 ? moments.indexOf(KEY_MOMENT[part]) : moments.indexOf('release'), 0, maxU);
  if (u < 0) u = 0;
  // controls that only make sense with depth
  if (!world || !fwd) sec.querySelectorAll('[data-preset="side"], [data-preset="top"]').forEach((b) => { b.disabled = true; });
  if (!world) sec.querySelectorAll('[data-turn]').forEach((b) => { b.disabled = true; });
  if (!world) sec.querySelector('.bm-drag').textContent = `${R.flat} ${R.ideal}`;
  scrub.max = String(maxU);
  scrub.value = String(u);
  const ticks = sec.querySelector('.bm-ticks');
  ticks.innerHTML = moments.map((k, i) => `<span data-l="${maxU ? ((i / maxU) * 100).toFixed(1) : '0'}">${esc(R.ticks[k])}</span>`).join('');
  applyStyles(ticks);

  // the part's own picture of what good looks like, and the words for the canvas
  const hl = highlightFor(part, J);
  let Wc = 320, Hc = 320, dpr = 1, F = 1, cx = 160, cy = 160, grad = null;
  function size() {
    const w = Math.round(cv.clientWidth || 320);
    if (!w) return false;
    dpr = Math.min(2, window.devicePixelRatio || 1);
    Wc = w; Hc = w;
    cv.width = Math.round(Wc * dpr); cv.height = Math.round(Hc * dpr);
    // The whole shot, from the dip to the highest reach, fills 80 percent of
    // the height as the phone saw it (3.5 fits a 1.8 m body the same way; this
    // fits the player's own size and jump), centred, with the floor ring in
    // the room left under the feet. A tiny extent (legs out of the picture) is
    // never blown up past a 1.2 m body.
    const du = Math.max(fitBox.u1 - fitBox.u0, 0.25 / CAM_D), dv = Math.max(fitBox.v1 - fitBox.v0, 1.2 / CAM_D);
    F = Math.min((0.8 * Hc) / dv, (0.84 * Wc) / du);
    cx = Wc / 2 - ((fitBox.u0 + fitBox.u1) / 2) * F;
    cy = Hc * 0.47 - ((fitBox.v0 + fitBox.v1) / 2) * F;
    grad = g2.createLinearGradient(0, 0, 0, Hc);
    grad.addColorStop(0, '#FFA24D'); grad.addColorStop(1, '#FF5A1F');
    return true;
  }
  const proj = new Float32Array(33 * 3);
  function project(x, y, z, out, o) {
    const a = cam.yaw * DEG, b = cam.tilt * DEG;
    const ca = Math.cos(a), sa = Math.sin(a), cb = Math.cos(b), sb = Math.sin(b);
    const x1 = x * ca + z * sa, z1 = -x * sa + z * ca;
    const y2 = y * cb - z1 * sb, z2 = y * sb + z1 * cb;
    const k = F / (CAM_D + z2);
    out[o] = cx + x1 * k; out[o + 1] = cy + y2 * k; out[o + 2] = z2;
    return k;
  }
  const pt = new Float32Array(3);
  const P2 = (p) => { project(p.x, p.y, p.z, pt, 0); return [pt[0], pt[1], pt[2]]; };
  /** Points at slider position t, linearly between the two nearest key frames. */
  function pointsAt(t) {
    const i0 = Math.floor(t), i1 = Math.min(maxU, i0 + 1), f = t - i0;
    const a = P[moments[i0]], b = P[moments[i1]];
    const out = new Float32Array(33 * 4);
    for (let j = 0; j < out.length; j++) out[j] = a[j] + (b[j] - a[j]) * f;
    return out;
  }
  const at = (pts, i) => ({ x: pts[i * 4], y: pts[i * 4 + 1], z: pts[i * 4 + 2] });
  const nearest = () => moments[Math.round(u)];

  function draw() {
    const t0 = performance.now();
    if (!grad && !size()) return;
    const c = g2;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.clearRect(0, 0, Wc, Hc);
    const pts = pointsAt(u);
    for (const i of KEEP_POINTS) project(pts[i * 4], pts[i * 4 + 1], pts[i * 4 + 2], proj, i * 3);
    // the floor, with the take off spot and the landing
    floor(c);
    const tk = U.frames.takeoff ? P.takeoff : null, ld = U.frames.land ? P.land : null;
    if (tk) spotOnFloor(c, (tk[27 * 4] + tk[28 * 4]) / 2, (tk[27 * 4 + 2] + tk[28 * 4 + 2]) / 2, 0.17, 'rgba(255,255,255,.10)', null);
    if (ld) spotOnFloor(c, (ld[27 * 4] + ld[28 * 4]) / 2, (ld[27 * 4 + 2] + ld[28 * 4 + 2]) / 2, 0.035, 'rgba(255,90,31,.9)', null);
    // bones far to near, near ones thicker
    const bones = BONES.map(([a, b]) => ({ a, b, z: (proj[a * 3 + 2] + proj[b * 3 + 2]) / 2 }));
    const hc = headCentre(pts);
    const neck = { x: (pts[11 * 4] + pts[12 * 4]) / 2, y: (pts[11 * 4 + 1] + pts[12 * 4 + 1]) / 2, z: (pts[11 * 4 + 2] + pts[12 * 4 + 2]) / 2 };
    let zmin = Infinity, zmax = -Infinity;
    for (const bn of bones) { if (bn.z < zmin) zmin = bn.z; if (bn.z > zmax) zmax = bn.z; }
    const span = Math.max(zmax - zmin, 0.05);
    const wid = (z) => 5 - ((z - zmin) / span) * 3;
    bones.sort((p, q) => q.z - p.z);
    c.lineCap = 'round'; c.lineJoin = 'round';
    c.strokeStyle = grad;
    for (const bn of bones) {
      const va = pts[bn.a * 4 + 3], vb = pts[bn.b * 4 + 3];
      c.globalAlpha = Math.min(va, vb) < 0.5 ? 0.35 : 1;
      c.lineWidth = wid(bn.z);
      c.beginPath(); c.moveTo(proj[bn.a * 3], proj[bn.a * 3 + 1]); c.lineTo(proj[bn.b * 3], proj[bn.b * 3 + 1]); c.stroke();
    }
    c.globalAlpha = 1;
    // neck and head
    const [nx, ny] = P2(neck), [hx, hy, hz] = P2(hc);
    c.lineWidth = 4; c.beginPath(); c.moveTo(nx, ny); c.lineTo(hx, hy); c.stroke();
    const hr = HEAD_R * (F / (CAM_D + hz));
    c.fillStyle = 'rgba(255,122,50,.18)'; c.beginPath(); c.arc(hx, hy, hr, 0, Math.PI * 2); c.fill();
    c.lineWidth = 2.5; c.stroke();
    const nose = at(pts, 0);
    if (fin(nose.x)) { const [ox, oy] = P2(nose); c.fillStyle = '#FFA24D'; c.beginPath(); c.arc(ox, oy, 2.6, 0, Math.PI * 2); c.fill(); }
    // joints
    for (const i of KEEP_POINTS) {
      if (i === 0 || i === 7 || i === 8) continue;
      c.globalAlpha = pts[i * 4 + 3] < 0.5 ? 0.35 : 1;
      c.fillStyle = '#FFB27A';
      c.beginPath(); c.arc(proj[i * 3], proj[i * 3 + 1], 2.8, 0, Math.PI * 2); c.fill();
    }
    c.globalAlpha = 1;
    // the chosen part, in white with a soft glow
    const glowLine = (x1, y1, x2, y2, w) => {
      c.strokeStyle = 'rgba(255,255,255,.22)'; c.lineWidth = w + 9; c.beginPath(); c.moveTo(x1, y1); c.lineTo(x2, y2); c.stroke();
      c.strokeStyle = '#fff'; c.lineWidth = w; c.beginPath(); c.moveTo(x1, y1); c.lineTo(x2, y2); c.stroke();
    };
    for (const [a, b] of hl.bones) glowLine(proj[a * 3], proj[a * 3 + 1], proj[b * 3], proj[b * 3 + 1], 4.2);
    if (hl.spine) { const hm = P2(mid3(at(pts, 23), at(pts, 24))); glowLine(nx, ny, hm[0], hm[1], 4.2); }
    if (hl.head) { c.strokeStyle = 'rgba(255,255,255,.25)'; c.lineWidth = 10; c.beginPath(); c.arc(hx, hy, hr, 0, Math.PI * 2); c.stroke(); c.strokeStyle = '#fff'; c.lineWidth = 3; c.stroke(); }
    for (const i of hl.joints) {
      c.fillStyle = 'rgba(255,255,255,.25)'; c.beginPath(); c.arc(proj[i * 3], proj[i * 3 + 1], 10, 0, Math.PI * 2); c.fill();
      c.fillStyle = '#fff'; c.beginPath(); c.arc(proj[i * 3], proj[i * 3 + 1], 4.6, 0, Math.PI * 2); c.fill();
    }
    ideal(c, pts, hc);
    const ms = performance.now() - t0;
    stats.drawMs = ms; stats.drawMax = Math.max(stats.drawMax, ms); stats.draws++;
    if (hcv) drawHand();
  }
  function headCentre(pts) {
    const e7 = at(pts, 7), e8 = at(pts, 8), n = at(pts, 0);
    const em = fin(e7.x) && fin(e8.x) ? mid3(e7, e8) : n;
    return fin(n.x) ? { x: em.x + (n.x - em.x) * 0.35, y: em.y + (n.y - em.y) * 0.35 - 0.02, z: em.z + (n.z - em.z) * 0.35 } : em;
  }
  function ring(c, x, y, z, r, n = 28) {
    c.beginPath();
    for (let i = 0; i <= n; i++) { const a = (i / n) * Math.PI * 2; const [sx, sy] = P2({ x: x + Math.cos(a) * r, y, z: z + Math.sin(a) * r }); if (i) c.lineTo(sx, sy); else c.moveTo(sx, sy); }
  }
  function floor(c) {
    ring(c, 0, floorY, 0, 0.8, 36);
    c.fillStyle = 'rgba(255,255,255,.04)'; c.fill();
    c.strokeStyle = 'rgba(255,255,255,.14)'; c.lineWidth = 1; c.stroke();
    c.strokeStyle = 'rgba(255,255,255,.06)';
    for (let g = -0.6; g <= 0.61; g += 0.3) {
      const h = Math.sqrt(Math.max(0, 0.64 - g * g));
      let a = P2({ x: g, y: floorY, z: -h }), b = P2({ x: g, y: floorY, z: h });
      c.beginPath(); c.moveTo(a[0], a[1]); c.lineTo(b[0], b[1]); c.stroke();
      a = P2({ x: -h, y: floorY, z: g }); b = P2({ x: h, y: floorY, z: g });
      c.beginPath(); c.moveTo(a[0], a[1]); c.lineTo(b[0], b[1]); c.stroke();
    }
  }
  function spotOnFloor(c, x, z, r, fillC, strokeC) {
    ring(c, x, floorY, z, r, 20);
    if (fillC) { c.fillStyle = fillC; c.fill(); }
    if (strokeC) { c.strokeStyle = strokeC; c.stroke(); }
  }
  /** The ideal line for the part, dashed Sky (3.5 table). */
  function ideal(c, pts, hc) {
    c.save();
    c.strokeStyle = SKY; c.fillStyle = SKY; c.lineWidth = 2; c.setLineDash([6, 5]); c.lineCap = 'round';
    const line = (p, q) => { const a = P2(p), b = P2(q); c.beginPath(); c.moveTo(a[0], a[1]); c.lineTo(b[0], b[1]); c.stroke(); };
    const add = (p, d, k) => ({ x: p.x + d.x * k, y: p.y + d.y * k, z: p.z + d.z * k });
    const upV = { x: 0, y: -1, z: 0 };
    const E = at(pts, J.E), Wr = at(pts, J.W), S = at(pts, J.S);
    switch (part) {
      case 'elbow': {
        if (!fwd || !fin(E.x)) break;
        const len = Math.hypot(Wr.x - E.x, Wr.y - E.y, Wr.z - E.z) || 0.26;
        const dir = norm(add(scale(fwd, Math.cos(62 * DEG)), upV, Math.sin(62 * DEG)));
        line(E, add(E, dir, len * 1.35));
        c.setLineDash([2, 4]); line(E, add(E, fwd, 0.13));
        c.setLineDash([]);
        c.beginPath();
        for (let i = 0; i <= 16; i++) { const a = (i / 16) * 62 * DEG; const p = P2(add(add(E, fwd, 0.09 * Math.cos(a)), upV, 0.09 * Math.sin(a))); if (i) c.lineTo(p[0], p[1]); else c.moveTo(p[0], p[1]); }
        c.stroke();
        break;
      }
      case 'wrist': case 'fingers': {
        if (!fwd || !fin(E.x) || !fin(Wr.x)) break;
        const fa = norm(sub(Wr, E));
        let perp = sub(fwd, scale(fa, dot(fwd, fa)));
        if (Math.hypot(perp.x, perp.y, perp.z) < 0.1) break;
        perp = norm(perp);
        const tgt = norm(add(scale(fa, Math.cos(60 * DEG)), perp, Math.sin(60 * DEG)));
        line(Wr, add(Wr, tgt, part === 'fingers' ? 0.2 : 0.13));
        break;
      }
      case 'shoulders': {
        const a = at(pts, 11), b = at(pts, 12);
        if (!fin(a.x) || !fin(b.x)) break;
        const mm = mid3(a, b), w = Math.hypot(b.x - a.x, b.z - a.z) || 0.35;
        const dir = norm({ x: b.x - a.x, y: 0, z: b.z - a.z });
        line(add(mm, dir, -w * 0.8), add(mm, dir, w * 0.8));
        if (snap.rl) { c.setLineDash([2, 5]); line(add(mm, { x: 1, y: 0, z: 0 }, -w * 0.8), add(mm, { x: 1, y: 0, z: 0 }, w * 0.8)); }
        break;
      }
      case 'head': {
        if (fwd) line(hc, add(hc, fwd, 0.4));
        if (P.set) { const q = P2(headCentre(P.set)); c.setLineDash([]); c.beginPath(); c.arc(q[0], q[1], 4, 0, Math.PI * 2); c.stroke(); }
        break;
      }
      case 'core': {
        const hm = mid3(at(pts, 23), at(pts, 24));
        line({ x: hm.x, y: floorY, z: hm.z }, { x: hm.x, y: topY - 0.05, z: hm.z });
        break;
      }
      case 'hips': {
        const a = P.takeoff || P.load, b = P.land || null;
        if (a) { const hm = mid3(at(a, 23), at(a, 24)); c.setLineDash([]); ring(c, hm.x, floorY, hm.z, 0.16, 24); c.stroke(); }
        if (b) { const hm = mid3(at(b, 23), at(b, 24)); c.beginPath(); const q = P2({ x: hm.x, y: floorY, z: hm.z }); c.arc(q[0], q[1], 4.5, 0, Math.PI * 2); c.fill(); }
        break;
      }
      case 'knees': {
        for (const [hp, tp] of [[23, 31], [24, 32]]) { const a = at(pts, hp), b = at(pts, tp); if (fin(a.x) && fin(b.x)) line(a, b); }
        break;
      }
      case 'ankles': case 'heels': {
        for (const k of ['load', 'takeoff']) {
          const f = P[k]; if (!f) continue;
          c.globalAlpha = k === 'load' ? 0.55 : 0.9;
          for (const [a, h, t] of [[27, 29, 31], [28, 30, 32]]) {
            const pa2 = P2(at(f, a)), ph = P2(at(f, h)), ptt = P2(at(f, t));
            c.beginPath(); c.moveTo(pa2[0], pa2[1]); c.lineTo(ph[0], ph[1]); c.lineTo(ptt[0], ptt[1]); c.closePath(); c.stroke();
          }
        }
        c.globalAlpha = 1;
        break;
      }
      case 'toes': {
        if (fwd) {
          const f = P.load || pts;
          for (const t of [31, 32]) {
            const p0 = { ...at(f, t), y: floorY }, p1 = add(p0, fwd, 0.42);
            line(p0, p1);
            const s = P2(p1), side2 = { x: fwd.z, y: 0, z: -fwd.x };
            const l1 = P2(add(add(p1, fwd, -0.08), side2, 0.04)), l2 = P2(add(add(p1, fwd, -0.08), side2, -0.04));
            c.setLineDash([]); c.beginPath(); c.moveTo(l1[0], l1[1]); c.lineTo(s[0], s[1]); c.lineTo(l2[0], l2[1]); c.stroke(); c.setLineDash([6, 5]);
          }
        }
        const tk2 = P.takeoff;
        if (tk2) { c.setLineDash([]); const x = (tk2[27 * 4] + tk2[28 * 4]) / 2, z = (tk2[27 * 4 + 2] + tk2[28 * 4 + 2]) / 2; ring(c, x, floorY, z, 0.2, 24); c.stroke(); }
        break;
      }
      default: break;
    }
    c.restore();
  }

  // the hand, flat, with the goose neck finish to aim for as a ghost (fingers and wrist)
  const handKs = moments.filter((k) => U.frames[k].h);
  const iFollow = moments.indexOf('follow');
  let hbox = null;
  if (hcv) {
    const note = sec.querySelector('.bm-handnote');
    if (!handKs.length) { hcv.hidden = true; note.textContent = R.noHand; }
    else {
      const sideOn = Boolean((U.view === 'side' || U.view === 'angled') && facing);
      note.textContent = `${R.hand}.${sideOn ? ` ${R.handGhost}` : ''}`;
      hcv.setAttribute('aria-label', note.textContent);
      hbox = handBox(sideOn);
    }
  }
  /** One hand frame as drawn, relative to its wrist in square picture units: 21 points, the forearm direction, the palm length. */
  function handShape(k) {
    const f = U.frames[k], h = f.h;
    const pts = [];
    for (let j = 0; j < 21; j++) pts.push({ x: (h[j * 3] - h[0]) * aspect, y: h[j * 3 + 1] - h[1] });
    const e = img(f, J.E), w = img(f, J.W);
    return { pts, fa: norm2({ x: w.x - e.x, y: w.y - e.y }), L: Math.hypot(pts[9].x, pts[9].y) || 0.02 };
  }
  /** The goose neck finish to aim for: the hand bent 60 degrees off the forearm toward the rim, from wrist W with palm length L. */
  function ghostOf(fa, L, W) {
    const th = 60 * DEG * facing;
    const hdv = { x: fa.x * Math.cos(th) - fa.y * Math.sin(th), y: fa.x * Math.sin(th) + fa.y * Math.cos(th) };
    const acr = { x: -hdv.y, y: hdv.x };
    const pnt = (al, ac) => ({ x: W.x + (hdv.x * al + acr.x * ac) * L, y: W.y + (hdv.y * al + acr.y * ac) * L });
    return { knuckle: pnt(0.95, 0), fingers: [-0.3, -0.1, 0.1, 0.3].map((ac) => [pnt(1, ac), pnt(1.85 - Math.abs(ac) * 0.6, ac * 1.1)]) };
  }
  /** One scale and one wrist spot for every hand frame, so scrubbing bends the hand about a still wrist. */
  function handBox(sideOn) {
    const shapes = handKs.map(handShape);
    let x0 = 0, x1 = 0, y0 = 0, y1 = 0;
    const grow = (p) => { if (p.x < x0) x0 = p.x; if (p.x > x1) x1 = p.x; if (p.y < y0) y0 = p.y; if (p.y > y1) y1 = p.y; };
    for (const s of shapes) {
      s.pts.forEach(grow);
      if (sideOn && fin(s.fa.x)) { const gh = ghostOf(s.fa, s.L, { x: 0, y: 0 }); grow(gh.knuckle); for (const [kn, tip] of gh.fingers) { grow(kn); grow(tip); } }
    }
    const k = (160 * 0.78) / Math.max(x1 - x0, y1 - y0, 1e-4);
    return { shapes, sideOn, k, x: 80 - ((x0 + x1) / 2) * k, y: 80 - ((y0 + y1) / 2) * k };
  }
  function drawHand() {
    if (!hbox || hcv.hidden) return;
    const c = hcv.getContext('2d');
    const S = 160, hd = Math.min(2, window.devicePixelRatio || 1);
    if (hcv.width !== S * hd) { hcv.width = S * hd; hcv.height = S * hd; }
    c.setTransform(hd, 0, 0, hd, 0, 0);
    c.clearRect(0, 0, S, S);
    // the two hand frames around the slider, by moment
    const pos = handKs.map((k) => moments.indexOf(k));
    let a = 0; while (a < pos.length - 1 && pos[a + 1] <= u) a++;
    const b = Math.min(pos.length - 1, u > pos[a] ? a + 1 : a);
    const f = pos[b] === pos[a] ? 0 : clamp((u - pos[a]) / (pos[b] - pos[a]), 0, 1);
    const A = hbox.shapes[a], Bh = hbox.shapes[b], k = hbox.k;
    const pt = (j) => ({ x: hbox.x + (A.pts[j].x + (Bh.pts[j].x - A.pts[j].x) * f) * k, y: hbox.y + (A.pts[j].y + (Bh.pts[j].y - A.pts[j].y) * f) * k });
    const fa = norm2({ x: A.fa.x + (Bh.fa.x - A.fa.x) * f, y: A.fa.y + (Bh.fa.y - A.fa.y) * f });
    const L = (A.L + (Bh.L - A.L) * f) * k;
    const W = { x: hbox.x, y: hbox.y };
    c.lineCap = 'round'; c.lineJoin = 'round';
    // the forearm, from the pose, into the wrist; it runs off the edge
    if (fin(fa.x)) { c.strokeStyle = 'rgba(255,162,77,.30)'; c.lineWidth = L * 0.62; c.beginPath(); c.moveTo(W.x - fa.x * S, W.y - fa.y * S); c.lineTo(W.x, W.y); c.stroke(); }
    // the goose neck finish to aim for: a Sky hand under the player's, strongest at the follow through
    const gh = hbox.sideOn && fin(fa.x) ? ghostOf(fa, L, W) : null;
    const ga = iFollow < 0 ? 1 : 0.45 + 0.55 * clamp(1 - Math.abs(u - iFollow), 0, 1);
    if (gh) {
      c.strokeStyle = `rgba(46,168,255,${(0.3 * ga).toFixed(3)})`;
      c.lineWidth = L * 0.8; c.beginPath(); c.moveTo(W.x, W.y); c.lineTo(gh.knuckle.x, gh.knuckle.y); c.stroke();
      c.lineWidth = L * 0.26;
      for (const [kn, tip] of gh.fingers) { c.beginPath(); c.moveTo(kn.x, kn.y); c.lineTo(tip.x, tip.y); c.stroke(); }
    }
    // the player's hand: palm and fingers as thick strokes, then the bones and the joints
    c.strokeStyle = 'rgba(255,162,77,.42)';
    c.lineWidth = L * 0.8; c.beginPath(); c.moveTo(pt(0).x, pt(0).y); for (const j of [5, 9, 13, 17]) c.lineTo(pt(j).x, pt(j).y); c.closePath(); c.stroke();
    c.lineWidth = L * 0.26;
    for (const fg of [[1, 2, 3, 4], [5, 6, 7, 8], [9, 10, 11, 12], [13, 14, 15, 16], [17, 18, 19, 20]]) {
      c.beginPath(); c.moveTo(pt(fg[0]).x, pt(fg[0]).y); for (const j of fg.slice(1)) c.lineTo(pt(j).x, pt(j).y); c.stroke();
    }
    c.strokeStyle = '#FF7A32'; c.lineWidth = 2.6;
    for (const [p, q] of HAND_BONES) { const s = pt(p), e = pt(q); c.beginPath(); c.moveTo(s.x, s.y); c.lineTo(e.x, e.y); c.stroke(); }
    for (let j = 0; j < 21; j++) {
      const tip = j === 4 || j === 8 || j === 12 || j === 16 || j === 20, p = pt(j);
      c.fillStyle = tip && part === 'fingers' ? '#fff' : j === 0 && part === 'wrist' ? '#fff' : '#FFB27A';
      c.beginPath(); c.arc(p.x, p.y, tip || j === 0 ? 3.4 : 2.2, 0, Math.PI * 2); c.fill();
    }
    // ...and the finish to aim for again, dashed on top, so both show where they overlap
    if (gh) {
      c.save();
      c.strokeStyle = SKY; c.globalAlpha = ga; c.lineWidth = 1.6; c.setLineDash([4, 4]);
      c.beginPath(); c.moveTo(W.x, W.y); c.lineTo(gh.knuckle.x, gh.knuckle.y); c.stroke();
      for (const [kn, tip] of gh.fingers) { c.beginPath(); c.moveTo(kn.x, kn.y); c.lineTo(tip.x, tip.y); c.stroke(); }
      c.restore();
    }
  }

  // ---- words on and around the canvas
  function describe() {
    const k = nearest();
    const name = R.moments[k];
    tagEl.textContent = name;
    scrub.setAttribute('aria-valuetext', name);
    let detail = '';
    const pts = P[k];
    if (part === 'elbow' && world) { const v2 = angle3(at(pts, J.S), at(pts, J.E), at(pts, J.W)); if (fin(v2)) detail = fill(C.elbowAt, { v: Math.round(v2) }); }
    else if (part === 'knees' && world) {
      const l = angle3(at(pts, 23), at(pts, 25), at(pts, 27)), r = angle3(at(pts, 24), at(pts, 26), at(pts, 28));
      if (fin(l) && fin(r)) detail = fill(C.kneesAt, { v: Math.round((l + r) / 2) });
    }
    cv.setAttribute('aria-label', fill(R.aria, { moment: name.toLowerCase(), detail }).replace(/\s+/g, ' ').trim());
    const turned = Math.abs(wrap180(cam.yaw)) > DEPTH_NOTE_AT || Math.abs(cam.tilt) > DEPTH_NOTE_AT;
    depthEl.classList.toggle('on', world && turned);
  }

  // ---- drawing on demand only
  let raf = 0;
  const request = () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; draw(); }); };
  let playing = 0;
  function stopPlay() { if (playing) { cancelAnimationFrame(playing); playing = 0; } playBtn.classList.remove('on'); playBtn.setAttribute('aria-label', R.play); }
  function play() {
    if (playing) { stopPlay(); return; }
    const t0 = performance.now();
    playBtn.classList.add('on'); playBtn.setAttribute('aria-label', R.pause);
    const step = (now) => {
      const x = clamp((now - t0) / PLAY_MS, 0, 1);
      u = maxU * (x < 0.5 ? 2 * x * x : 1 - (-2 * x + 2) ** 2 / 2);
      scrub.value = String(u);
      draw(); describe();
      if (x < 1) playing = requestAnimationFrame(step); else { playing = 0; stopPlay(); }
    };
    playing = requestAnimationFrame(step);
  }
  let tween = 0;
  // where the last turn was headed: a turn tapped mid tween builds on it, not on a half finished angle
  const goal = { yaw: cam.yaw, tilt: cam.tilt };
  function turnTo(yaw, tilt) {
    cancelAnimationFrame(tween);
    goal.yaw = wrap180(yaw); goal.tilt = tilt;
    const from = { yaw: cam.yaw, tilt: cam.tilt };
    const dy = wrap180(yaw - from.yaw);
    if (reduced()) { cam.yaw = wrap180(from.yaw + dy); cam.tilt = tilt; request(); describe(); return; }
    const t0 = performance.now();
    const step = (now) => {
      const x = clamp((now - t0) / 380, 0, 1), e = x < 0.5 ? 2 * x * x : 1 - (-2 * x + 2) ** 2 / 2;
      cam.yaw = from.yaw + dy * e; cam.tilt = from.tilt + (tilt - from.tilt) * e;
      draw(); describe();
      if (x < 1) tween = requestAnimationFrame(step); else { tween = 0; cam.yaw = wrap180(cam.yaw); }
    };
    tween = requestAnimationFrame(step);
  }
  const onScrub = () => { stopPlay(); u = clamp(Number(scrub.value) || 0, 0, maxU); request(); describe(); };
  scrub.addEventListener('input', onScrub);
  playBtn.addEventListener('click', play);
  const onTurn = (e) => {
    const b = e.target.closest('button'); if (!b || b.disabled) return;
    if (b.dataset.turn) turnTo(goal.yaw + (b.dataset.turn === 'left' ? 30 : -30), goal.tilt);
    else if (b.dataset.preset === 'camera') turnTo(0, 0);
    else if (b.dataset.preset === 'side') turnTo((side === 'left' ? -90 : 90) - psiF / DEG, 0);
    else if (b.dataset.preset === 'top') turnTo(-psiF / DEG, TILT_TOP);
  };
  const turns = sec.querySelector('.bm-turns');
  turns.addEventListener('click', onTurn);
  // drag: sideways spins, up and down tilts (clamped), pointer events for touch and mouse
  let drag = null;
  const down = (e) => { if (!world) return; drag = { x: e.clientX, y: e.clientY, yaw: cam.yaw, tilt: cam.tilt, id: e.pointerId }; cv.setPointerCapture?.(e.pointerId); cv.classList.add('grab'); stopPlay(); cancelAnimationFrame(tween); };
  const move = (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const hi = drag.tilt > TILT_DRAG ? TILT_TOP : TILT_DRAG;
    cam.yaw = drag.yaw - (e.clientX - drag.x) * 0.55;
    cam.tilt = clamp(drag.tilt + (e.clientY - drag.y) * 0.4, -TILT_DRAG, hi);
    goal.yaw = wrap180(cam.yaw); goal.tilt = cam.tilt;
    request(); describe();
  };
  const up = (e) => { if (!drag || e.pointerId !== drag.id) return; drag = null; cv.classList.remove('grab'); };
  cv.addEventListener('pointerdown', down);
  cv.addEventListener('pointermove', move);
  cv.addEventListener('pointerup', up);
  cv.addEventListener('pointercancel', up);
  const obs = typeof ResizeObserver === 'function' ? new ResizeObserver(() => { if (size()) draw(); }) : null;
  obs?.observe(cv);
  if (size()) draw();
  describe();
  return {
    destroy() {
      stopPlay(); cancelAnimationFrame(tween); cancelAnimationFrame(raf); raf = 0;
      obs?.disconnect();
      scrub.removeEventListener('input', onScrub); playBtn.removeEventListener('click', play); turns.removeEventListener('click', onTurn);
      cv.removeEventListener('pointerdown', down); cv.removeEventListener('pointermove', move); cv.removeEventListener('pointerup', up); cv.removeEventListener('pointercancel', up);
    },
    get u() { return u; },
    draw,
  };
}
// vector kit for the replay
const sub = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const scale = (a, k) => ({ x: a.x * k, y: a.y * k, z: a.z * k });
const cross = (a, b) => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const norm = (a) => { const l = Math.hypot(a.x, a.y, a.z) || 1e-9; return { x: a.x / l, y: a.y / l, z: a.z / l }; };
const norm2 = (a) => { const l = Math.hypot(a.x, a.y) || 1e-9; return { x: a.x / l, y: a.y / l }; };
const mid3 = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: (a.z + b.z) / 2 });

/* ======================================================================= *
 *  CELEBRATIONS (3.7): one at a time, each once                            *
 * ======================================================================= */
/** Queue celebrations: [{ part, from, to, xp }] for a part that climbed, [{ badge }] for a new badge, [{ badges }] for several. */
export function celebrate(list) {
  for (const c of list || []) if (c && (c.part || c.badge || Array.isArray(c.badges))) queue.push({ ...c, key: c.key || `manual:${Math.random()}` });
  if (!celebrating) nextCelebration();
}
function nextCelebration() {
  // The phone sheet covers the screen and the chip would sit on its headline:
  // the next one waits until the sheet is closed.
  if (queue.length && root && ui.sheet && ui.layout === 'phone') { celebrating = true; setTimeout(nextCelebration, 500); return; }
  const c = queue.shift();
  if (!c || !root) { celebrating = false; return; }
  celebrating = true;
  const keys = c.badges ? c.badges.map((id) => `badge:${id}`) : [c.badge ? `badge:${c.badge}` : c.key || `${c.part}:${c.to}`];
  for (const k of keys) played.add(k);
  const chip = root.querySelector('.bm-chip');
  if (!chip) { celebrating = false; return; }
  let text, sub, part = null;
  if (c.badges) {
    const list = c.badges.map((id) => COPY.badges[id]).filter(Boolean);
    if (!list.length) { nextCelebration(); return; }
    const tier = ['gold', 'silver', 'bronze'].find((t) => list.some((b) => b.tier === t));
    text = fill(COPY.badgesNew, { n: list.length });
    sub = fill(COPY.badgesMore, { names: list.slice(0, 2).map((b) => b.name).join(', '), n: list.length - 2 });
    chip.innerHTML = `${MEDAL(tier, false)}<span><b class="bm-chip-h">${esc(text)}</b><small>${esc(sub)}</small></span>`;
  } else if (c.badge) {
    const b = COPY.badges[c.badge]; if (!b) { nextCelebration(); return; }
    text = fill(COPY.badgeNew, { name: b.name }); sub = b.blurb;
    chip.innerHTML = `${MEDAL(b.tier, false)}<span><b class="bm-chip-h">${esc(text)}</b><small>${esc(sub)}</small></span>`;
    part = b.part;
  } else {
    part = c.part;
    text = fill(COPY.celebrate, { part: lower(part), from: WORD[c.from] || c.from, to: WORD[c.to] || c.to });
    sub = fill(COPY.celebrateXp, { xp: c.xp ?? (c.from === 'work' ? BODY.XP_JUMP : BODY.XP_TIER) });
    chip.innerHTML = `${mark('good', 30)}<span><b class="bm-chip-h">${esc(text)}</b><small>${esc(sub)}</small></span>`;
  }
  chip.dataset.part = part || '';
  chip.dataset.go = c.badges ? 'badges' : '';
  chip.classList.remove('on'); void chip.offsetWidth; chip.classList.add('on');
  if (part) {
    const spot = root.querySelector(`.bm-spot[data-part="${part}"]`), row = root.querySelector(`.bm-row[data-part="${part}"]`);
    for (const el of [spot, row]) { if (!el) continue; el.classList.remove('bm-pulse'); void el.getBoundingClientRect(); el.classList.add('bm-pulse'); }
    if (!reduced() && spot) burst(part);
  }
  setTimeout(() => { chip.classList.remove('on'); setTimeout(nextCelebration, 380); }, CELEBRATE_MS);
}
/** 16 small particles in orange and Sky from the part, CSS transforms only, 900 ms. */
function burst(part) {
  const box = root.querySelector('.bm-figbox'), layer = root.querySelector('.bm-burst'), svg = root.querySelector('.bm-fig');
  if (!box || !layer || !svg) return;
  const s = SPOT[part];
  const k = svg.getBoundingClientRect().width / 360;
  const x0 = s.kind === 'pair' ? 180 : s.x, y0 = s.kind === 'pair' ? s.pts[0][1] : s.y;
  const x = (D.side === 'left' ? 360 - x0 : x0) * k, y = y0 * k;
  const bits = document.createDocumentFragment();
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2 + (i % 2 ? 0.2 : 0);
    const r = 46 + (i % 3) * 16;
    const bit = document.createElement('span');
    bit.className = `bm-bit${i % 2 ? ' sky' : ''}`;
    bit.style.left = `${x.toFixed(1)}px`; bit.style.top = `${y.toFixed(1)}px`;
    bit.style.setProperty('--dx', `${(Math.cos(a) * r).toFixed(1)}px`); bit.style.setProperty('--dy', `${(Math.sin(a) * r).toFixed(1)}px`);
    bits.appendChild(bit);
  }
  layer.replaceChildren(bits);
  setTimeout(() => { if (layer.isConnected) layer.replaceChildren(); }, 1000);
}

/* ======================================================================= *
 *  IN THE APP                                                              *
 * ======================================================================= */
/** From app.js mountModules: the shared context ({ api }). Optional. */
export function mount(c) { ctx = c || null; }

/** Where the player said the phone is ('rim' | 'side' | 'other' | null), for app.js onShot (BODY-MAP.md 1.5). */
export function placement() { const p = store.get(PLACEMENT_KEY); return ['rim', 'side', 'other'].includes(p) ? p : null; }

const hasBaseline = () => Boolean(store.get(BASELINE_KEY));

/** For app.js go('body'): fetch the player's Body Map and draw it into #v-body. */
export async function show() {
  const el = typeof document !== 'undefined' ? document.getElementById('v-body') : null;
  if (!el) return;
  if (root !== el || !D) render(el, { loading: true });
  try {
    const data = ctx?.api ? await ctx.api('/api/bodymap') : await fetch('/api/bodymap', { headers: { 'content-type': 'application/json' } }).then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); });
    render(el, { ...data, calibrated: data?.calibrated || hasBaseline() });
  } catch {
    render(el, { error: true });
  }
}
